import { useEffect, useCallback, useRef, useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useVoiceStore } from '../../stores/voiceStore'
import { useLivekitStore } from '../../stores/livekitStore'
import { useServerStore } from '@/shell/stores/serverStore'
import { useAuthStore } from '../../stores/authStore'
import { formatPhoneDisplay } from '../../utils/formatPhone'
import { useGuestStore } from '../../stores/guestStore'
import { useParticipantActionsStore } from '../../stores/participantActionsStore'
import { channelApi } from '../../services/api'
import { useTranscription } from '../../hooks/useTranscription'
import { useLocalAudioLevel } from '../../hooks/useLocalAudioLevel'
import { useTileVideoDimensions } from '../../hooks/useTileVideoDimensions'
import { getSocket } from '../../services/socket'
import { dialog } from '../../services/dialog'
import { readBandwidthSuppression } from '../../services/connectionQualityWatcher'
import { getUserDisplayName } from '@os/messenger/web/components/telegram/TelegramChatList'
import { mediaUrl } from '../../services/config'
import { displayInitial } from '../../utils/displayInitial'
import { compareParticipants } from '../../utils/participantSort'
import { detectClientInfo } from '../../utils/clientInfo'
import { Track, type RemoteParticipant, type LocalParticipant } from 'livekit-client'

import { Mic, MicOff, HeadphoneOff, Phone, SignalHigh, Video, VideoOff, LayoutGrid, Presentation, Maximize, Pin, Loader2, Hand, Hourglass, ArrowUp, ArrowDown, Globe, Laptop, Smartphone, WifiOff, MoreVertical, History as HistoryIcon, Lock, Volume2, Focus } from 'lucide-react'
import { useParticipantBandwidth, useSelfBandwidth, formatBandwidth } from './useParticipantBandwidth'
import { hasRenderableVideo, isSelfCameraRenderable, attachSelfPreview } from './videoTileState'
import AudioMeter from './AudioMeter'
import ResizableSidePanel from './ResizableSidePanel'
import ScreenShareAnnotation from './ScreenShareAnnotation'
import RemoteControlOverlay from './RemoteControlOverlay'
import PollOverlay from './PollOverlay'
import PresentationOverlay from './PresentationOverlay'
import RaisedHandsPanel from './RaisedHandsPanel'
import { useRemoteControlStore } from '../../stores/remoteControlStore'
import { VoiceBirthdayCelebration } from './VoiceBirthdayCelebration'
import { BirthdayBadge } from '../birthday/BirthdayBadge'
import { DialInChip } from './DialInChip'
import { useEmotionRatings } from './useEmotionRatings'
import { useEmotionRatingStore } from '../../stores/emotionRatingStore'

interface TileLayout {
  cols: number
  rows: number
  tileClass: string
}

const MIN_TILE_WIDTH = 320
const MIN_TILE_HEIGHT = 220
const MAX_COLS = 5
// Debounce before pausing an off-screen tile's video subscription, so a fast
// scroll-through doesn't churn subscribe/unsubscribe signaling + keyframes.
// Re-subscribe is immediate (no debounce) so video is ready as a tile enters.
const OFFSCREEN_PAUSE_DEBOUNCE_MS = 1500
// Off-screen video pausing only kicks in on BIG calls — that's the only place
// decoding every tile saturates the CPU (2026-05-22: ~24 VP9 decodes). Below
// this many participants every machine decodes all tiles fine, so pausing would
// only add a re-subscribe blink when you scroll (e.g. an 11-person call) for no
// benefit. Tunable live: localStorage 'voiceOffscreenPauseMinParticipants'.
const OFFSCREEN_PAUSE_MIN_PARTICIPANTS_DEFAULT = 14
function offscreenPauseMinParticipants(): number {
  if (typeof localStorage === 'undefined') return OFFSCREEN_PAUSE_MIN_PARTICIPANTS_DEFAULT
  const v = parseInt(localStorage.getItem('voiceOffscreenPauseMinParticipants') || '', 10)
  return Number.isFinite(v) && v > 0 ? v : OFFSCREEN_PAUSE_MIN_PARTICIPANTS_DEFAULT
}

export function getTileLayout(count: number, containerWidth: number): TileLayout {
  if (count <= 0) return { cols: 1, rows: 1, tileClass: '' }
  const colsByWidth = Math.max(1, Math.floor(containerWidth / MIN_TILE_WIDTH))
  // Preferred grid — picks balanced 2x2 / 2x3 / 2x4 instead of
  // stretching a single row. 4 people on a wide screen used to render
  // as a 1x4 strip with cropped faces; this forces 2x2. The user
  // reported that going from 6 to 7 thumbnails in the speaker-side
  // grid kept stretching the same row instead of wrapping — at 7 we
  // explicitly drop to 4 cols so it splits into 4+3 rather than 5+2
  // (which still left tiles squeezed) or 7+0 (single-row pinch).
  const preferredCols: Record<number, number> = {
    1: 1,
    2: 2,
    3: 3,
    4: 2, // 2x2 — never a 1x4 strip
    5: 3, // 3 + 2
    6: 3, // 3x2
    7: 4, // 4 + 3 — second row, balanced
    8: 4, // 4x2
    9: 3, // 3x3
    10: 4, // 4 + 4 + 2
    11: 4, // 4 + 4 + 3
    12: 4, // 4x3
  }
  const desired = preferredCols[count] ?? Math.min(count, MAX_COLS)
  const cols = Math.max(1, Math.min(desired, MAX_COLS, colsByWidth))
  const rows = Math.ceil(count / cols)
  return { cols, rows, tileClass: '' }
}

function getParticipantStream(participant: RemoteParticipant | LocalParticipant): MediaStream | null {
  const tracks: MediaStreamTrack[] = []
  participant.getTrackPublications().forEach((pub) => {
    if (pub.track && pub.track.mediaStreamTrack) {
      const source = pub.source
      if (source === Track.Source.Microphone || source === Track.Source.Camera) {
        tracks.push(pub.track.mediaStreamTrack)
      }
    }
  })
  return tracks.length > 0 ? new MediaStream(tracks) : null
}

function getScreenShareStream(participant: RemoteParticipant | LocalParticipant): MediaStream | null {
  const pub = participant.getTrackPublication(Track.Source.ScreenShare)
  if (pub?.track?.mediaStreamTrack) {
    return new MediaStream([pub.track.mediaStreamTrack])
  }
  return null
}

export default function VoiceChannelView() {
  const { serverId, channelId } = useParams()
  const navigate = useNavigate()
  const server = useServerStore((s) => s.servers.find((sv) => sv.id === serverId))
  const channel = server?.channels.find((c) => c.id === channelId)
  const currentUser = useAuthStore((s) => s.user)
  const isAdmin = currentUser?.isAdmin === true || (server as any)?.ownerId === currentUser?.id
  const isDeafened = currentUser?.settings?.isDeafened ?? false
  const {
    currentChannelId,
    joinedAt,
    participants,
    joinChannel,
    viewMode,
    setViewMode,
    currentSpeakerId,
    previousSpeakerId,
    setCurrentSpeakerId,
    setPreviousSpeakerId,
    pinnedPrimaryId,
    pinnedSecondaryId,
    setPinnedIds,
  } = useVoiceStore()
  // OSPROD-64: drives the Grid-view split layout.
  const pinnedUserIds = useVoiceStore((s) => s.pinnedUserIds)
  const livekitRoom = useLivekitStore((s) => s.room)
  const livekitRemotes = useLivekitStore((s) => s.remoteParticipants)
  const rcSession = useRemoteControlStore((s) => s.session)
  // Subscribe to the local audio/video/screen tracks so the tree
  // re-renders when the user toggles their mic/camera/screen share.
  // Without this, getStream() runs against a stale snapshot of the
  // LiveKit room and:
  //   - the local <video> element never picks up the new camera track,
  //   - hasScreenShare doesn't flip back to false when the user ends
  //     screen sharing via the browser's "Stop sharing" button (which
  //     unpublishes the track but doesn't otherwise touch React state),
  //     leaving the annotation canvas stranded over a blank tile.
  // The local mic meter has its own LiveKit-driven path
  // (useLocalAudioLevel) so it doesn't depend on this anymore, but the
  // video & screen tiles still do.
  const localAudioTrack = useLivekitStore((s) => s.localAudioTrack)
  const localVideoTrack = useLivekitStore((s) => s.localVideoTrack)
  const localScreenTrack = useLivekitStore((s) => s.localScreenTrack)
  // Camera takes ~500-1500ms to come up — getUserMedia + LiveKit publish.
  // Use the optimistic flag for self so the tile flips to "starting" the
  // moment the user clicks the camera button, not when LiveKit finishes
  // publishing. Powers the loading-spinner overlay in VoiceTile.
  const optimisticVideoEnabled = useLivekitStore((s) => s.optimisticVideoEnabled)
  // OSPROD-60: when on, drop the local self-camera tile from the grid.
  // Camera publish stays untouched — purely a local visibility toggle.
  const isSelfViewHidden = useVoiceStore((s) => s.isSelfViewHidden)
  // Sticky ownership (2026-07-07): set by livekitStore.connect()'s pre-check
  // when another live device owns this channel's voice. Drives the view-only
  // banner + "Move here" hand-off below.
  const voiceActiveElsewhere = useVoiceStore((s) => s.voiceActiveElsewhere)
  const takeoverVoiceHere = useLivekitStore((s) => s.takeoverVoiceHere)
  // OSPROD-73: when the bottom chat/transcript strip is collapsed, the
  // wrapper drops from h-72 (288px) to h-9 (36px) — just enough for
  // the tab bar — and the voice grid above takes the freed space.
  // OSPROD-115: the bottom chat/transcript strip is now a resizable
  // wrapper (ResizableSidePanel) that owns its own height + collapse
  // handling, so the old fixed h-9 / h-72 class is gone.
  void localAudioTrack
  void localVideoTrack
  void localScreenTrack
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const focusedTileRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(1200)
  const [joinError, setJoinError] = useState<string | null>(null)
  const [focusedUserId, setFocusedUserId] = useState<string | null>(null)
  const { startTranscription, stopTranscription } = useTranscription(channelId)

  // Voice-host status — gates the Kick/Ban actions in the per-tile
  // participant context menu (Unit D store). Fetched once per channel;
  // the menu itself still re-checks server-side on every moderation call.
  const [isHost, setIsHost] = useState(false)
  useEffect(() => {
    if (!channelId) { setIsHost(false); return }
    let cancelled = false
    channelApi.hostStatus(channelId)
      .then((res) => { if (!cancelled) setIsHost(res.data.isHost) })
      .catch(() => { if (!cancelled) setIsHost(false) })
    return () => { cancelled = true }
  }, [channelId])

  // Voice channels in this server — powers the "Move to…" action in the
  // participant menu. Empty array when not in a server (e.g. DM voice).
  const voiceChannels = useMemo(
    () => (server?.channels ?? [])
      .filter((c) => c.type === 'VOICE')
      .map((c) => ({ id: c.id, name: c.name })),
    [server],
  )

  // Bundle the moderation-menu context once and spread it into every
  // <VoiceTile>. Keeps the ~20 call sites to a single extra prop and
  // avoids each tile re-fetching host status. Memoised so tiles don't
  // re-render on unrelated parent updates.
  const tileMenuCtx = useMemo(
    () => ({ channelId, serverId: serverId ?? null, isHost, voiceChannels }),
    [channelId, serverId, isHost, voiceChannels],
  )

  // Callback ref for the grid container — re-attaches the ResizeObserver
  // every time the element is mounted/remounted. Switching viewMode
  // (speaker ↔ grid) unmounts the grid container; the previous useEffect
  // with empty deps only saw the FIRST element it ran against, so after
  // a Speaker→Grid switch the new container went unobserved and
  // containerWidth stayed at the initial 1200 (or worse, 0 from a
  // fleeting earlier measurement) — collapsing the layout to 1 column.
  const roRef = useRef<ResizeObserver | null>(null)
  const gridContainerRef = useCallback((el: HTMLDivElement | null) => {
    if (roRef.current) {
      roRef.current.disconnect()
      roRef.current = null
    }
    if (!el) return
    // Seed once with the current width so we get a correct layout on
    // the first paint (ResizeObserver fires asynchronously).
    const initialWidth = el.getBoundingClientRect().width
    if (initialWidth > 0) setContainerWidth(initialWidth)
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width
        if (w > 0) setContainerWidth(w)
      }
    })
    ro.observe(el)
    roRef.current = ro
  }, [])

  const isInChannel = currentChannelId === channelId
  const isMuted = !livekitRoom?.localParticipant.isMicrophoneEnabled

  // Speaker scoring with audio-level awareness
  const speakerScoreRef = useRef<Record<string, number>>({})
  const livekitRoomRef = useRef(livekitRoom)
  useEffect(() => { livekitRoomRef.current = livekitRoom }, [livekitRoom])

  useEffect(() => {
    if (!isInChannel) {
      speakerScoreRef.current = {}
      setCurrentSpeakerId(null)
      setPreviousSpeakerId(null)
      return
    }

    const SPEAKING_THRESHOLD = 0.05
    const PROMOTION_SCORE = 20 // 20 * 100ms = 2 seconds of sustained audio
    const MAX_SCORE = 60 // cap accumulator so a long monologue can be unseated
    const PROMOTION_LOCK_MS = 2000 // ignore further swaps for 2s after one fires

    let lastPromotionAt = 0
    const interval = setInterval(() => {
      const room = livekitRoomRef.current
      if (!room) return
      const store = useVoiceStore.getState()
      const currentId = store.currentSpeakerId
      const scores = speakerScoreRef.current

      let bestOverall: string | null = null
      let bestOverallScore = -1
      let bestChallenger: string | null = null
      let bestChallengerScore = -1

      const processParticipant = (userId: string, audioLevel: number) => {
        if (audioLevel > SPEAKING_THRESHOLD) {
          scores[userId] = Math.min(MAX_SCORE, (scores[userId] || 0) + 1)
        } else {
          scores[userId] = Math.max(0, (scores[userId] || 0) - 0.5)
        }
        const score = scores[userId] || 0
        if (score > bestOverallScore) {
          bestOverallScore = score
          bestOverall = userId
        }
        if (userId !== currentId && score > bestChallengerScore) {
          bestChallengerScore = score
          bestChallenger = userId
        }
      }

      // Local participant
      const localUserId = currentUser?.id || 'self'
      processParticipant(localUserId, room.localParticipant.audioLevel || 0)

      // Remote participants
      room.remoteParticipants.forEach((remote) => {
        processParticipant(remote.identity, remote.audioLevel || 0)
      })

      // Lock the current speaker for PROMOTION_LOCK_MS after a swap. Without
      // this, two people speaking simultaneously cause the spotlight to
      // ping-pong every 100ms tick because their accumulators stay above the
      // threshold within a tick of each other — visible as 5+ seconds of
      // strobing tile swaps. The lock plus a 2s build-up means a real
      // takeover still happens promptly but in-the-noise oscillation can't.
      const now = Date.now()
      const lockExpired = now - lastPromotionAt > PROMOTION_LOCK_MS
      if (lockExpired && bestChallenger && bestChallengerScore >= PROMOTION_SCORE) {
        store.setPreviousSpeakerId(currentId)
        store.setCurrentSpeakerId(bestChallenger)
        lastPromotionAt = now
        // Reset the new current speaker's score so the immediate next tick
        // can't promote a third party who happened to be at MAX_SCORE — the
        // freshly-promoted person is the spotlight, not whoever else was
        // also talking.
        scores[bestChallenger] = 0
      }

      // Fallback: nobody is current yet — pick whoever has any speaking
      // activity at all so the speaker view isn't stuck on the join order.
      if (!currentId && bestOverall && bestOverallScore > 0) {
        store.setCurrentSpeakerId(bestOverall)
      }

      // Cleanup disconnected users
      const activeIds = new Set([localUserId, ...Array.from(room.remoteParticipants.keys())])
      Object.keys(scores).forEach((id) => {
        if (!activeIds.has(id)) delete scores[id]
      })
    }, 100)

    return () => clearInterval(interval)
  }, [isInChannel, currentUser?.id, setCurrentSpeakerId, setPreviousSpeakerId])

  // Timer cleanup
  useEffect(() => {
    if (isInChannel && joinedAt) {
      return () => { if (timerRef.current) clearInterval(timerRef.current) }
    }
  }, [isInChannel, joinedAt])

  // Start/stop transcription on join/leave and mute/unmute. Depends on
  // isAudioTrackReady too: setMicrophoneEnabled is async, so when the
  // user undeafens / unmutes, isMuted flips before the track is
  // actually live. startTranscription bails out in that window — we
  // re-fire when isAudioTrackReady ticks to true so transcription
  // resumes automatically.
  const isAudioTrackReadyForTx = useLivekitStore((s) => s.isAudioTrackReady)
  useEffect(() => {
    if (isInChannel && !isMuted && isAudioTrackReadyForTx) {
      startTranscription()
    } else {
      stopTranscription()
    }
    return () => { stopTranscription() }
  }, [isInChannel, isMuted, isAudioTrackReadyForTx, startTranscription, stopTranscription])

  // No history fetch on join. The previous version emitted
  // `voice:getTranscripts` here and overwrote the freshly-cleared
  // `voiceStore.transcripts` with whatever the server returned — but the
  // server filters by the requesting user's `voiceState.sessionId`, which
  // (a) hasn't been written yet at this point because `voice:join` is
  // awaited behind the LiveKit connect, so sessionId resolves to null and
  // the query degrades to `WHERE sessionId IS NULL` matching every
  // guest-authored or pre-migration transcript on this channel; (b) can
  // alias to a stale-session UUID via the existingStates re-use path in
  // socket.ts. Either way the panel filled with old data.
  //
  // Live transcripts arrive via the `transcript:new` socket event (handled
  // in useTranscription.ts) — that's enough for the conversation in the
  // current session. A history button could re-introduce on-demand fetch
  // later if needed, but it must not run on join.

  const handleJoin = useCallback(async (micPolicy?: 'unmute') => {
    const guestState = useGuestStore.getState()
    const userId = guestState.isGuest ? guestState.guestToken?.slice(6) : currentUser?.id
    const username = guestState.isGuest
      ? (guestState.displayName || 'Guest')
      : (currentUser?.username || currentUser?.id || 'User')
    if (!channelId || !userId) return
    const { currentChannelId: activeId, audioInputDeviceId, isNoiseSuppressionOn } = useVoiceStore.getState()
    setJoinError(null)
    // Optimistic enter: flip UI into the channel immediately so clicks feel
    // snappy. The actual LiveKit connect + socket emit run in the background;
    // if either fails we revert and surface a banner.
    const defaultLayout = channel?.defaultLayout === 'SPEAKER' ? 'speaker' : 'grid'
    setViewMode(defaultLayout)
    joinChannel(channelId, serverId, micPolicy ? { micPolicy } : undefined)
    ;(async () => {
      try {
        if (activeId && activeId !== channelId) {
          // Tell the server we're leaving the old room so its broadcast
          // updates everyone else's UI immediately. Without this the
          // user appears in two rooms at once until the server's voice
          // disconnect handler eventually fires.
          getSocket().emit('voice:leave', { channelId: activeId })
          await useLivekitStore.getState().disconnect()
        }
        // No takeover option → the connect() pre-check runs (sticky
        // ownership). If another live device owns this channel's voice the
        // gate refuses to touch LiveKit, leaves room === null, and sets
        // voiceActiveElsewhere → the banner below renders and the user can
        // choose "Move here". Only when the gate let us through (room is
        // truthy) do we register presence.
        await useLivekitStore.getState().connect(
          channelId,
          username,
          audioInputDeviceId,
          isNoiseSuppressionOn
        )
        if (useLivekitStore.getState().room) {
          // We truly hold voice now. takeover:true here is a DELIBERATE join —
          // disconnect any of this user's OTHER devices/tabs from voice
          // (single active device). Reconnect re-joins in VoiceManager
          // intentionally omit this flag.
          getSocket().emit('voice:join', { channelId, userId, takeover: true })
        }
      } catch (err) {
        console.error('Failed to join voice:', err)
        setJoinError('Could not join voice channel')
        // Revert the optimistic UI change so the user can try again.
        useVoiceStore.getState().leaveChannel()
      }
    })()
  }, [channelId, currentUser, channel?.defaultLayout, setViewMode, joinChannel])

  // Sticky ownership (2026-07-07): confirm-gated hand-off. Voice is currently
  // held on another device (voiceActiveElsewhere set). "Move here" asks for
  // explicit consent, then re-connects with { takeover: true } (bypasses the
  // pre-check) and sends the explicit takeover join so the server evicts the
  // old device (voiceTakeover.ts) + broadcasts voice:disconnected-elsewhere.
  const handleMoveHere = useCallback(async () => {
    const active = useVoiceStore.getState().voiceActiveElsewhere
    const ok = await dialog.confirm({
      title: 'Voice already active',
      message: active
        ? `You're connected to voice on ${active.deviceLabel}. Move your voice to this device?`
        : 'Move your voice to this device?',
      confirmLabel: 'Move here',
      tone: 'default',
    })
    if (!ok) return
    const { audioInputDeviceId, isNoiseSuppressionOn, currentChannelId } = useVoiceStore.getState()
    const username = currentUser?.username || currentUser?.id || 'User'
    const cid = currentChannelId || active?.channelId || channelId
    const userId = currentUser?.id
    if (!cid || !userId) return
    await takeoverVoiceHere(cid, username, audioInputDeviceId, isNoiseSuppressionOn)
    getSocket().emit('voice:join', { channelId: cid, userId, takeover: true })
    getSocket().emit('voice:getParticipants', { channelId: cid })
  }, [currentUser, channelId, takeoverVoiceHere])

  // OSPROD-104: click-to-join. When the user clicks a voice channel in the
  // list (and has the setting on), ChannelList parks the channel id here;
  // we consume it once on mount and fire the normal join flow — but only
  // if we're not already connected to this room (clicking the current room
  // must be a no-op, no mic re-publish). Scoped to the explicit click via
  // the one-shot intent, so a refresh / deep-link does NOT auto-join.
  const pendingAutoJoin = useVoiceStore((s) => s.pendingAutoJoinChannelId)
  useEffect(() => {
    if (!channelId || pendingAutoJoin !== channelId) return
    // Capture the one-shot mic policy BEFORE clearing — setPendingAutoJoin(null)
    // resets it together with the channel id.
    const micPolicy = useVoiceStore.getState().pendingAutoJoinMicPolicy
    useVoiceStore.getState().setPendingAutoJoin(null)
    if (useVoiceStore.getState().currentChannelId !== channelId) {
      handleJoin(micPolicy ?? undefined)
    } else if (micPolicy === 'unmute') {
      // Already connected (e.g. admitted a phone caller into the room we're
      // in) — no re-join, but the answer-the-phone semantics still apply.
      useAuthStore.getState().unmuteForPhoneAdmit()
    }
  }, [pendingAutoJoin, channelId, handleJoin])

  const channelParticipants = useMemo(
    () => participants.filter((p) => p.channelId === channelId),
    [participants, channelId]
  )

  // F3: batch-fetch emotional ratings for all current participants so each
  // NormalVoiceTile can read from the store without its own network call.
  const participantUserIds = useMemo(
    () => [...new Set(channelParticipants.map((p) => p.userId).filter((id): id is string => !!id && !id.startsWith('guest_')))],
    [channelParticipants],
  )
  // OSPROD-220: only global admins / server admins may see the in-call
  // emotional badge. Skip the fetch for everyone else — the server is the
  // authoritative gate (returns {} for non-admins), this is defense-in-depth
  // that also avoids a pointless request. `canManagePeople` is a superset of
  // the server's per-server admin check, so this never hides the badge from a
  // legitimate admin/server-admin.
  const canViewEmotion = !!(currentUser?.isAdmin || currentUser?.role === 'ADMIN' || currentUser?.canManagePeople)
  useEmotionRatings(canViewEmotion ? participantUserIds : [])

  const getScreenShareTrackSid = useCallback((p: any): string => {
    if (!livekitRoom) return ''
    if (p.isSelf) {
      const pub = livekitRoom.localParticipant.getTrackPublication(Track.Source.ScreenShare)
      return pub?.trackSid ?? ''
    }
    const baseId = p.userId.replace('-screen', '')
    const remote = livekitRoom.remoteParticipants.get(baseId)
    const pub = remote?.getTrackPublication(Track.Source.ScreenShare)
    return pub?.trackSid ?? ''
  }, [livekitRoom])

  const getConnectionState = useCallback((p: any): { label: string; color: string; icon?: React.ReactNode } | undefined => {
    // For the local user we don't need a "LOCAL" connection badge — the
    // "You" name tag is already enough, and the extra label was floating
    // in empty white space and confusing users about what it meant.
    // The one exception: while LiveKit is still negotiating (room hasn't
    // been created yet), surface a "Connecting…" pill so the user knows
    // the optimistic self-tile isn't actually publishing yet.
    if (p.isSelf) {
      if (!livekitRoom) return { label: 'Connecting…', color: '#fbbf24' }
      return undefined
    }
    const remote = livekitRoom?.remoteParticipants.get(p.userId.replace('-screen', ''))
    if (!remote) return { label: 'Disconnected', color: '#72767d' }
    return { label: 'Connected', color: '#3ba55d', icon: <SignalHigh size={10} /> }
  }, [livekitRoom])

  const allParticipants = useMemo(() => {
    if (!isInChannel) {
      // Lobby preview — sort the channel-participants list so an
      // outsider sees the same predictable order as anyone inside.
      return channelParticipants
        .slice()
        .sort(compareParticipants)
        .map((p) => ({ ...p, isSelf: false, isScreenShareOnly: false, identity: undefined as string | undefined, isGuest: false }))
    }
    // OSPROD-60: drop the self-camera tile from the grid when the user
    // has hidden their self-view. Camera keeps publishing — peers still
    // see us — only the local rendering is filtered. Screen-share tile
    // is intentionally NOT hidden: the user needs to see what they're
    // broadcasting.
    //
    // OSPROD-82: tile order — self pinned first (Zoom/Meet/Discord
    // convention; never moves under you), then remote participants via
    // the shared compareParticipants (department-first grouping +
    // locale-aware firstName tie-break + userId total order — see
    // utils/participantSort.ts), then screen-share tiles (self first,
    // then remote screens) sorted the same way. Reconnects, voice
    // activity, mute toggles, and screen-share starts no longer shuffle
    // the grid — LiveKit's natural insertion order was driving the
    // visible jumps.
    // OSPROD-81: client info for the self tiles. Prefer the server-echoed
    // value from the channel participant list (same source remotes use) —
    // it carries the async-upgraded nativeBuild (Electron/Capacitor build)
    // and the Brave label, so my own tile shows the full version sub-line
    // just like peers see mine. Fall back to a fresh local sync detect
    // before the echo arrives (or if self isn't in the list yet); that
    // still has the label + appVersion, just not nativeBuild.
    const selfClientInfo =
      channelParticipants.find((cp) => cp.userId === currentUser?.id)?.clientInfo
      ?? detectClientInfo()
    // Self-bandwidth suppression flag rides on our own LiveKit
    // participant metadata (set by ConnectionQualityWatcher when our
    // connection went Poor). Same source as the remote-side read so
    // self + peers see the chip on identical conditions.
    const selfBandwidthSuppressed = readBandwidthSuppression(
      livekitRoom?.localParticipant.metadata ?? null,
    ).suppressed
    const selfTile = isSelfViewHidden ? [] : [{
      userId: currentUser?.id || 'self',
      user: currentUser || { username: 'You', avatarUrl: null },
      isSelf: true,
      isMuted: !livekitRoom?.localParticipant.isMicrophoneEnabled,
      isDeafened,
      isSpeaking: livekitRoom?.localParticipant.isSpeaking || false,
      // Prefer optimistic flag — drives the spinner overlay in VoiceTile
      // until LiveKit finishes publishing the camera track.
      isVideoOn: optimisticVideoEnabled ?? livekitRoom?.localParticipant.isCameraEnabled ?? false,
      isScreenSharing: livekitRoom?.localParticipant.isScreenShareEnabled || false,
      isScreenShareOnly: false,
      clientInfo: selfClientInfo,
      bandwidthSuppressed: selfBandwidthSuppressed,
      identity: livekitRoom?.localParticipant.identity,
      isGuest: false,
    }]
    const selfScreenTile = livekitRoom?.localParticipant.isScreenShareEnabled ? [{
      userId: (currentUser?.id || 'self') + '-screen',
      user: currentUser || { username: 'You', avatarUrl: null },
      isSelf: true,
      isGuest: false,
      isScreenShareOnly: true,
      isMuted: false,
      isDeafened: false,
      isSpeaking: false,
      isVideoOn: false,
      isScreenSharing: true,
      clientInfo: selfClientInfo,
      bandwidthSuppressed: false,
      // identity = LiveKit local participant identity (used by RemoteControlOverlay)
      identity: livekitRoom.localParticipant.identity,
    }] : []
    const remoteTiles = livekitRemotes.map((p) => {
      const socketP = channelParticipants.find((cp) => cp.userId === p.identity)
      const isGuest = p.identity.startsWith('guest_')
      return {
        userId: p.identity,
        user: socketP?.user || { username: p.name || p.identity, avatarUrl: null },
        isSelf: false,
        isGuest,
        isMuted: !p.isMicrophoneEnabled,
        isDeafened: socketP?.isDeafened || false,
        isSpeaking: p.isSpeaking,
        isVideoOn: p.isCameraEnabled,
        isScreenSharing: p.isScreenShareEnabled,
        isScreenShareOnly: false,
        // OSPROD-81: server attaches clientInfo to socket VoiceState
        // via attachClientInfo (apps/server/src/socket.ts) — forward it
        // through this synthesized tile so VoiceTile's platform badge
        // can render. Dropping it here is what hid the "macOS App" /
        // "Web / Chrome" chips for every remote participant.
        clientInfo: socketP?.clientInfo ?? null,
        // Read peer's bandwidth-suppression flag from LiveKit
        // participant metadata. Set by their own
        // ConnectionQualityWatcher when their connection went Poor /
        // Lost. We show a chip in their tile so the rest of the
        // call knows why their video disappeared.
        bandwidthSuppressed: readBandwidthSuppression(p.metadata).suppressed,
        // identity on camera tiles: present for union-type consistency with
        // screen-share tiles; not used by the RC affordance (RC is gated on
        // isScreenShareOnly which is false here).
        identity: p.identity as string | undefined,
      }
    }).sort(compareParticipants)
    const remoteScreenTiles = livekitRemotes
      .filter((p) => p.isScreenShareEnabled)
      .map((p) => {
        const socketP = channelParticipants.find((cp) => cp.userId === p.identity)
        return {
          userId: p.identity + '-screen',
          user: socketP?.user || { username: p.name || p.identity, avatarUrl: null },
          isSelf: false,
          isGuest: p.identity.startsWith('guest_'),
          isScreenShareOnly: true,
          isMuted: false,
          isDeafened: false,
          isSpeaking: false,
          isVideoOn: false,
          isScreenSharing: true,
          clientInfo: socketP?.clientInfo ?? null,
          bandwidthSuppressed: false,
          // identity = LiveKit remote participant identity (publish target for RemoteControlOverlay)
          identity: p.identity,
        }
      })
      .sort(compareParticipants)
    return [
      ...selfTile,
      ...selfScreenTile,
      ...remoteTiles,
      ...remoteScreenTiles,
    ]
    // localAudioTrack/localVideoTrack/localScreenTrack must be in the
    // deps even though the body doesn't reference them directly: it
    // reads `livekitRoom.localParticipant.is{Microphone,Camera,ScreenShare}Enabled`,
    // which are properties of an object whose identity never changes
    // when LiveKit updates them. Without these track-identity deps, the
    // memo returns a stale array — most visibly: stopping screen share
    // via the browser's "Stop sharing" button leaves the screen-share
    // entry in `allParticipants`, which renders a duplicate empty tile
    // plus a stranded annotation canvas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInChannel, channelParticipants, currentUser, isDeafened, livekitRoom, livekitRemotes, localAudioTrack, localVideoTrack, localScreenTrack, optimisticVideoEnabled, isSelfViewHidden])

  // Auto-exit focus mode when focused participant leaves

  // Auto-exit focus mode when focused participant leaves
  useEffect(() => {
    if (!focusedUserId) return
    const focused = allParticipants.find((p) => p.userId === focusedUserId)
    if (!focused) {
      setFocusedUserId(null)
    }
  }, [allParticipants, focusedUserId])



  function getStream(p: any): MediaStream | null {
    if (!livekitRoom) return null
    if (p.isSelf) {
      if (p.isScreenShareOnly) {
        return getScreenShareStream(livekitRoom.localParticipant)
      }
      return getParticipantStream(livekitRoom.localParticipant)
    }
    const remote = livekitRoom.remoteParticipants.get(p.userId.replace('-screen', ''))
    if (!remote) return null
    if (p.isScreenShareOnly) {
      return getScreenShareStream(remote)
    }
    return getParticipantStream(remote)
  }

  // Speaker mode derived state (no duplicates, pin-aware)
  const realParticipants = allParticipants.filter((p) => !p.isScreenShareOnly)
  const currentSpeaker = (
    pinnedPrimaryId
      ? realParticipants.find((p) => p.userId === pinnedPrimaryId)
      : realParticipants.find((p) => p.userId === currentSpeakerId)
  ) || realParticipants[0] || null
  const previousSpeaker = (
    pinnedSecondaryId
      ? realParticipants.find((p) => p.userId === pinnedSecondaryId && p.userId !== currentSpeaker?.userId)
      : realParticipants.find((p) => p.userId === previousSpeakerId && p.userId !== currentSpeaker?.userId)
  ) || null
  // Presenter mode: screen shares are shown prominently
  const screenShares = allParticipants.filter((p) => p.isScreenShareOnly)
  const hasScreenShare = screenShares.length > 0
  const nonScreenParticipants = allParticipants.filter((p) => !p.isScreenShareOnly)
  // OSPROD-64: split for the multi-pin layout. When pinnedUserIds is
  // empty, both arrays are unused — we fall through to the regular grid.
  const pinnedTiles = nonScreenParticipants.filter((p) => pinnedUserIds.has(p.userId))
  const unpinnedTiles = nonScreenParticipants.filter((p) => !pinnedUserIds.has(p.userId))

  return (
    <div className="flex flex-col h-full bg-background min-w-0 relative">
      <VoiceBirthdayCelebration />
      {/* Live-event overlays: synchronized presentation (full-bleed) + poll
          (top-right card). Both are server-authoritative and coexist. */}
      {isInChannel && channelId && <PresentationOverlay channelId={channelId} isHost={isHost} />}
      {isInChannel && channelId && <PollOverlay channelId={channelId} isHost={isHost} />}
      {/* OSPROD-263: floating "Raised hands" queue over the right side of
          the call — auto-appears when >= 1 hand is up. */}
      {isInChannel && <RaisedHandsPanel participants={realParticipants} />}
      {/* Remote control controller banner — shown while actively controlling a peer's screen */}
      {rcSession.state === 'active' && rcSession.role === 'controller' && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 px-4 py-2 rounded-full bg-card border border-border shadow">
          <span className="text-sm text-foreground">You're controlling <b>{rcSession.peerName}</b>'s screen</span>
          <span className="text-xs text-muted-foreground">Admin windows can't be controlled</span>
          <button onClick={() => useRemoteControlStore.getState().end('controller-release')} className="px-3 py-1 rounded-md bg-muted hover:bg-muted/70 text-sm transition">Release</button>
        </div>
      )}
      {/* Top bar with view mode toggle — height & background match the
          sidebar's "Rx2Go" header so the chrome lines up across columns. */}
      {isInChannel && (
        <div className="h-14 bg-card flex items-center justify-end px-4 gap-2 shrink-0 border-b border-border">
          {channelId && <DialInChip channelId={channelId} />}
          <button
            title="Session history"
            onClick={() => navigate(`/channels/${channelId}/history`)}
            className="p-2 rounded-md hover:bg-accent text-muted-foreground"
          >
            <HistoryIcon size={18} />
          </button>
          <div className="flex items-center bg-muted rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('grid')}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition ${
                viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              }`}
            >
              <LayoutGrid size={14} />
              Grid
            </button>
            <button
              onClick={() => setViewMode('speaker')}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition ${
                viewMode === 'speaker' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              }`}
            >
              <Presentation size={14} />
              Speaker
            </button>
          </div>
        </div>
      )}

      {/* Main tile area */}
      <div className="flex-1 flex items-center justify-center p-4 min-h-0">
        {!isInChannel ? (
          <div className="flex flex-col items-center justify-center gap-6">
            {channelParticipants.length > 0 && (
              <div
                className="w-full flex flex-wrap items-center justify-center gap-3"
                style={{ maxWidth: getTileLayout(channelParticipants.length, containerWidth).cols * 600 + 'px' }}
              >
                {allParticipants.map((p) => (
                  <VoiceTile
                    key={p.userId}
                    {...tileMenuCtx}
                    participant={p}
                    isSelf={false}
                    stream={null}
                    layoutClass="w-full"
                    connectionState={getConnectionState(p)}
                  />
                ))}
              </div>
            )}
            <div className="text-center">
              <div className="w-20 h-20 bg-card rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone size={40} className="text-muted-foreground" />
              </div>
              <h2 className="text-xl font-bold text-foreground mb-2">{channel?.name || 'Voice Channel'}</h2>
              <p className="text-muted-foreground mb-4">
                {channelParticipants.length === 0
                  ? 'No one is in this voice channel yet'
                  : `${channelParticipants.length} participants`}
              </p>
              {voiceActiveElsewhere && voiceActiveElsewhere.channelId === channelId ? (
                // Sticky ownership: voice is held on another device. The plain
                // Join would just re-trip the pre-check gate (dead button), so
                // point the user at the "Move here" banner below (the confirm +
                // takeover hand-off) instead.
                <p className="text-sm text-muted-foreground mb-2">
                  Voice is active on another device — use “Move here” below to move it.
                </p>
              ) : (
                <button
                  onClick={() => handleJoin()}
                  className="px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white font-medium rounded-full transition"
                >
                  Join voice channel
                </button>
              )}
              {joinError && <p className="text-destructive mt-2 text-sm">{joinError}</p>}
            </div>
          </div>
        ) : focusedUserId ? (
          <div className="w-full h-full flex flex-col">
            {/* Thumbnail strip */}
            <div className="flex gap-2 overflow-x-auto p-2 shrink-0">
              {allParticipants.map((p) => (
                <div
                  key={p.userId}
                  onClick={() => setFocusedUserId(p.userId)}
                  className={`shrink-0 cursor-pointer rounded-lg overflow-hidden ${
                    focusedUserId === p.userId ? 'ring-2 ring-white' : 'opacity-70 hover:opacity-100'
                  }`}
                  style={{ width: '140px', aspectRatio: '16/9' }}
                >
                  {/* OSPROD-254: noAspect — the 140px wrapper already fixes the
                      16:9 box; letting the tile add its own aspect-video on top
                      of w-full h-full made its box fight the wrapper's. */}
                  <VoiceTile
                    {...tileMenuCtx}
                    participant={p}
                    isSelf={p.isSelf}
                    stream={getStream(p)}
                    layoutClass="w-full h-full"
                    cols={0}
                    noAspect
                    connectionState={getConnectionState(p)}
                  />
                </div>
              ))}
            </div>
            {/* Focused tile */}
            <div className="flex-1 flex items-center justify-center p-2 min-h-0">
              {(() => {
                const p = allParticipants.find((x) => x.userId === focusedUserId)
                if (!p) return null
                return (
                  <div className="relative w-full h-full">
                    <div
                      ref={focusedTileRef}
                      onClick={() => setFocusedUserId(null)}
                      className="w-full h-full cursor-pointer"
                    >
                      <VoiceTile
                        {...tileMenuCtx}
                        participant={p}
                        isSelf={p.isSelf}
                        stream={getStream(p)}
                        layoutClass="w-full h-full"
                        cols={0}
                        fit="contain"
                        noAspect
                        isHero
                        connectionState={getConnectionState(p)}
                      />
                    </div>
                    {p.isScreenShareOnly && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          focusedTileRef.current?.requestFullscreen()
                        }}
                        className="absolute top-4 right-4 z-20 w-10 h-10 rounded-lg bg-black/60 backdrop-blur-sm text-white hover:bg-black/80 flex items-center justify-center transition"
                        title="Fullscreen"
                      >
                        <Maximize size={20} />
                      </button>
                    )}
                  </div>
                )
              })()}
            </div>
          </div>
        ) : viewMode === 'speaker' ? (
          hasScreenShare ? (
            /* Screen share present: left = screen, right = participants + transcription */
            <div className="w-full h-full flex flex-row gap-3 min-h-0">
              {/* Screen share area */}
              <div className="flex-1 flex flex-col gap-3 min-h-0">
                {screenShares.map((p) => (
                  <ScreenShareAnnotation
                    key={p.userId}
                    channelId={channelId || ''}
                    sessionId={getScreenShareTrackSid(p)}
                    className="shrink-0 relative group flex-1 min-h-0"
                  >
                    <VoiceTile
                      {...tileMenuCtx}
                      participant={p}
                      isSelf={p.isSelf}
                      stream={getStream(p)}
                      layoutClass="w-full h-full"
                      cols={0}
                      connectionState={getConnectionState(p)}
                      fit="contain"
                      noAspect
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        e.currentTarget.parentElement?.requestFullscreen().catch(() => {})
                      }}
                      className="absolute top-3 right-3 z-20 w-9 h-9 rounded-lg bg-black/60 backdrop-blur-sm text-white hover:bg-black/80 flex items-center justify-center transition opacity-0 group-hover:opacity-100"
                      title="Fullscreen"
                    >
                      <Maximize size={18} />
                    </button>
                    {/* Remote control affordance — only for remote, non-guest screen-share
                        tiles whose sharer is on the desktop (Electron) app. A browser
                        controllee has no input-injection bridge (window.electronAPI.remoteControl),
                        so the request would be silently ignored — hide the button there. */}
                    {!p.isSelf && !p.isGuest && !!livekitRoom && !!p.identity && p.clientInfo?.isElectron && (
                      <>
                        {rcSession.state === 'idle' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              useRemoteControlStore.getState().request({
                                channelId: channelId || '',
                                serverId: serverId || '',
                                controlleeUserId: p.identity!,
                                controlleeName: p.user?.username || p.identity!,
                                controllerIdentity: useLivekitStore.getState().room?.localParticipant.identity || '',
                              })
                            }}
                            className="absolute top-3 left-3 z-30 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-xs hover:opacity-90 transition opacity-0 group-hover:opacity-100"
                          >
                            Request control
                          </button>
                        )}
                        <RemoteControlOverlay targetIdentity={p.identity!} />
                      </>
                    )}
                  </ScreenShareAnnotation>
                ))}
              </div>
              {/* Right: participants + transcription */}
              <div className="w-[260px] shrink-0 flex flex-col gap-3 min-h-0">
                <div className="flex-1 overflow-y-auto flex flex-wrap justify-center items-center content-center gap-3 p-1">
                  {nonScreenParticipants.map((p) => (
                    <VoiceTile
                      key={p.userId}
                      {...tileMenuCtx}
                      participant={p}
                      isSelf={p.isSelf}
                      stream={getStream(p)}
                      layoutClass="w-full"
                      cols={0}
                      onClick={() => setFocusedUserId(p.userId)}
                      connectionState={getConnectionState(p)}
                    />
                  ))}
                </div>
                {/* Transcription always visible */}
                <ResizableSidePanel channelId={channelId || ''} />
              </div>
            </div>
          ) : (
            /* No screen share: 3/8 left column stacks main speaker, last
               active speaker, and transcript; 5/8 right column shows a grid
               of every participant. */
            <div className="w-full h-full flex flex-row gap-3 min-h-0">
              {/* LEFT column — 3/8 */}
              <div className="flex-[3] min-w-0 flex flex-col gap-3 min-h-0">
                {/* Main speaker */}
                <div className="flex-1 min-h-0 relative group">
                  {currentSpeaker ? (
                    <div className="w-full h-full relative">
                      <VoiceTile
                        {...tileMenuCtx}
                        participant={currentSpeaker}
                        isSelf={currentSpeaker.isSelf}
                        stream={getStream(currentSpeaker)}
                        layoutClass="w-full h-full"
                        cols={0}
                        noAspect
                        isHero
                        connectionState={getConnectionState(currentSpeaker)}
                        onClick={() => setFocusedUserId(currentSpeaker.userId)}
                      />
                      {isAdmin && (
                        <div className="absolute top-2 left-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-20">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              const newId = pinnedPrimaryId === currentSpeaker.userId ? null : currentSpeaker.userId
                              setPinnedIds(newId, pinnedSecondaryId)
                              getSocket().emit('voice:pin', { channelId, targetUserId: currentSpeaker.userId, role: 'primary' })
                            }}
                            className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium backdrop-blur-sm transition ${pinnedPrimaryId === currentSpeaker.userId ? 'bg-primary text-primary-foreground' : 'bg-black/60 text-white hover:bg-black/80'}`}
                            title="Pin as Primary"
                          >
                            <Pin size={11} />
                            Primary
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              const newId = pinnedSecondaryId === currentSpeaker.userId ? null : currentSpeaker.userId
                              setPinnedIds(pinnedPrimaryId, newId)
                              getSocket().emit('voice:pin', { channelId, targetUserId: currentSpeaker.userId, role: 'secondary' })
                            }}
                            className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium backdrop-blur-sm transition ${pinnedSecondaryId === currentSpeaker.userId ? 'bg-primary text-primary-foreground' : 'bg-black/60 text-white hover:bg-black/80'}`}
                            title="Pin as Secondary"
                          >
                            <Pin size={11} />
                            Secondary
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="w-full h-full rounded-xl bg-card flex items-center justify-center text-muted-foreground text-sm">
                      Waiting for speaker...
                    </div>
                  )}
                </div>

                {/* Last active speaker — hidden if only one participant */}
                {realParticipants.length >= 2 && (
                  <div className="flex-1 min-h-0 relative group">
                    {previousSpeaker ? (
                      <div className="w-full h-full relative">
                        <VoiceTile
                          {...tileMenuCtx}
                          participant={previousSpeaker}
                          isSelf={previousSpeaker.isSelf}
                          stream={getStream(previousSpeaker)}
                          layoutClass="w-full h-full"
                          cols={0}
                          noAspect
                          isHero
                          connectionState={getConnectionState(previousSpeaker)}
                          onClick={() => setFocusedUserId(previousSpeaker.userId)}
                        />
                        {isAdmin && (
                          <div className="absolute top-2 left-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-20">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                const newId = pinnedPrimaryId === previousSpeaker.userId ? null : previousSpeaker.userId
                                setPinnedIds(newId, pinnedSecondaryId)
                                getSocket().emit('voice:pin', { channelId, targetUserId: previousSpeaker.userId, role: 'primary' })
                              }}
                              className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium backdrop-blur-sm transition ${pinnedPrimaryId === previousSpeaker.userId ? 'bg-primary text-primary-foreground' : 'bg-black/60 text-white hover:bg-black/80'}`}
                              title="Pin as Primary"
                            >
                              <Pin size={11} />
                              Primary
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                const newId = pinnedSecondaryId === previousSpeaker.userId ? null : previousSpeaker.userId
                                setPinnedIds(pinnedPrimaryId, newId)
                                getSocket().emit('voice:pin', { channelId, targetUserId: previousSpeaker.userId, role: 'secondary' })
                              }}
                              className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium backdrop-blur-sm transition ${pinnedSecondaryId === previousSpeaker.userId ? 'bg-primary text-primary-foreground' : 'bg-black/60 text-white hover:bg-black/80'}`}
                              title="Pin as Secondary"
                            >
                              <Pin size={11} />
                              Secondary
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      (() => {
                        const second = realParticipants.find((p) => p.userId !== currentSpeaker?.userId)
                        return second ? (
                          <div className="w-full h-full relative">
                            <VoiceTile
                              {...tileMenuCtx}
                              participant={second}
                              isSelf={second.isSelf}
                              stream={getStream(second)}
                              layoutClass="w-full h-full"
                              cols={0}
                              noAspect
                              connectionState={getConnectionState(second)}
                              onClick={() => setFocusedUserId(second.userId)}
                            />
                            {isAdmin && (
                              <div className="absolute top-2 left-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-20">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    const newId = pinnedPrimaryId === second.userId ? null : second.userId
                                    setPinnedIds(newId, pinnedSecondaryId)
                                    getSocket().emit('voice:pin', { channelId, targetUserId: second.userId, role: 'primary' })
                                  }}
                                  className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium backdrop-blur-sm transition ${pinnedPrimaryId === second.userId ? 'bg-primary text-primary-foreground' : 'bg-black/60 text-white hover:bg-black/80'}`}
                                  title="Pin as Primary"
                                >
                                  <Pin size={11} />
                                  Primary
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    const newId = pinnedSecondaryId === second.userId ? null : second.userId
                                    setPinnedIds(pinnedPrimaryId, newId)
                                    getSocket().emit('voice:pin', { channelId, targetUserId: second.userId, role: 'secondary' })
                                  }}
                                  className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium backdrop-blur-sm transition ${pinnedSecondaryId === second.userId ? 'bg-primary text-primary-foreground' : 'bg-black/60 text-white hover:bg-black/80'}`}
                                  title="Pin as Secondary"
                                >
                                  <Pin size={11} />
                                  Secondary
                                </button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="w-full h-full rounded-xl bg-card flex items-center justify-center text-muted-foreground text-sm">
                            No previous speaker
                          </div>
                        )
                      })()
                    )}
                  </div>
                )}

                {/* Transcription pinned at the bottom of the left column */}
                <ResizableSidePanel channelId={channelId || ''} />
              </div>

              {/* RIGHT column — 5/8 — grid of every participant who
                  isn't already shown big on the left. The user's rule:
                  a person is either in the left (primary or secondary)
                  OR in the right thumbnail strip — never both. The
                  previous code rendered duplicate tiles for promoted
                  speakers, eating room from the rest of the grid and
                  squeezing everyone else thin. */}
              <div className="flex-[5] min-w-0 min-h-0">
                {(() => {
                  const promotedIds = new Set<string>()
                  if (currentSpeaker) promotedIds.add(currentSpeaker.userId)
                  // OSPROD-117: the secondary big slot shows previousSpeaker,
                  // or — when there's no previous speaker — falls back to the
                  // first non-speaker (`second` in the left column above).
                  // Whoever actually fills that slot must be excluded here
                  // too, otherwise they render twice: the big tile AND a
                  // thumbnail in this grid (the reported duplicate).
                  const secondary = previousSpeaker
                    ?? realParticipants.find((p) => p.userId !== currentSpeaker?.userId)
                    ?? null
                  if (secondary) promotedIds.add(secondary.userId)
                  const sideParticipants = realParticipants.filter((p) => !promotedIds.has(p.userId))
                  const layout = getTileLayout(sideParticipants.length, containerWidth)
                  return (
                    <div
                      ref={gridContainerRef}
                      className="w-full h-full grid gap-3 justify-center overflow-y-auto"
                      style={{
                        gridTemplateColumns: `repeat(${layout.cols}, minmax(0, 1fr))`,
                        gridAutoRows: `minmax(${MIN_TILE_HEIGHT}px, 1fr)`,
                      }}
                    >
                      {sideParticipants.map((p) => (
                        <VoiceTile
                          key={p.userId}
                          {...tileMenuCtx}
                          participant={p}
                          isSelf={p.isSelf}
                          stream={getStream(p)}
                          layoutClass="w-full h-full"
                          cols={0}
                          noAspect
                          onClick={() => setFocusedUserId(p.userId)}
                          connectionState={getConnectionState(p)}
                        />
                      ))}
                    </div>
                  )
                })()}
              </div>
            </div>
          )
        ) : (
          <div className="w-full h-full flex flex-col gap-3 min-h-0">
            {/* Presenter mode: screen share on top, takes ~2/3 of available
                vertical space (`flex-[2]`). The previous version used
                `h-[50vh]` which is half the *viewport* height, not half the
                parent — on shorter parents (Mac with menu bar + header +
                bottom controls) the screen share overflowed and crushed
                the participant grid below into a stripe of overlapping
                tiles. flex-ratio sizing makes both sections proportional
                to whatever space is actually available. */}
            {hasScreenShare && (
              <div className="flex-[2] min-h-0 flex gap-3 justify-center">
                {screenShares.map((p) => (
                  <ScreenShareAnnotation
                    key={p.userId}
                    channelId={channelId || ''}
                    sessionId={getScreenShareTrackSid(p)}
                    className="relative group flex-1 min-h-0"
                  >
                    <VoiceTile
                      {...tileMenuCtx}
                      participant={p}
                      isSelf={p.isSelf}
                      stream={getStream(p)}
                      layoutClass="w-full h-full"
                      cols={0}
                      connectionState={getConnectionState(p)}
                      fit="contain"
                      noAspect
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        // Always fullscreen the wrapper so the canvas overlay
                        // and toolbar travel with the video. Fullscreening
                        // just <video> hides the annotation layer.
                        e.currentTarget.parentElement?.requestFullscreen().catch(() => {})
                      }}
                      className="absolute top-3 right-3 z-20 w-9 h-9 rounded-lg bg-black/60 backdrop-blur-sm text-white hover:bg-black/80 flex items-center justify-center transition opacity-0 group-hover:opacity-100"
                      title="Fullscreen"
                    >
                      <Maximize size={18} />
                    </button>
                    {/* Remote control affordance — only for remote, non-guest screen-share
                        tiles whose sharer is on the desktop (Electron) app. A browser
                        controllee has no input-injection bridge (window.electronAPI.remoteControl),
                        so the request would be silently ignored — hide the button there. */}
                    {!p.isSelf && !p.isGuest && !!livekitRoom && !!p.identity && p.clientInfo?.isElectron && (
                      <>
                        {rcSession.state === 'idle' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              useRemoteControlStore.getState().request({
                                channelId: channelId || '',
                                serverId: serverId || '',
                                controlleeUserId: p.identity!,
                                controlleeName: p.user?.username || p.identity!,
                                controllerIdentity: useLivekitStore.getState().room?.localParticipant.identity || '',
                              })
                            }}
                            className="absolute top-3 left-3 z-30 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-xs hover:opacity-90 transition opacity-0 group-hover:opacity-100"
                          >
                            Request control
                          </button>
                        )}
                        <RemoteControlOverlay targetIdentity={p.identity!} />
                      </>
                    )}
                  </ScreenShareAnnotation>
                ))}
              </div>
            )}
            {/* Grid of non-screen participants. With a single participant
                we drop into a speaker-like fill-the-whole-area layout — same
                as the Speaker view's primary tile — so the call doesn't show
                a tiny tile floating in a sea of background. */}
            {pinnedTiles.length > 0 ? (
              <>
                {/* Top strip: unpinned tiles, fixed-height row. */}
                {unpinnedTiles.length > 0 && (
                  // OSPROD-254: `justify-center` directly on an overflow-x-auto
                  // flex row clips the leftmost tiles once the strip overflows
                  // (the overflowing start edge becomes unreachable/cropped).
                  // Centering moved to an inner w-max/min-w-full wrapper: when
                  // the tiles fit it centers exactly as before; when they don't
                  // the strip scrolls from the first tile with nothing cut off.
                  <div className="h-32 shrink-0 overflow-x-auto px-1">
                    <div className="h-full w-max min-w-full flex flex-row gap-2 justify-center">
                      {unpinnedTiles.map((p) => (
                        <div key={p.userId} className="h-full aspect-video shrink-0">
                          <VoiceTile
                            {...tileMenuCtx}
                            participant={p}
                            isSelf={p.isSelf}
                            stream={getStream(p)}
                            layoutClass="w-full h-full"
                            cols={0}
                            noAspect
                            onClick={() => setFocusedUserId(p.userId)}
                            connectionState={getConnectionState(p)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Pinned area: rest of available height, regular grid math. */}
                {(() => {
                  const layout = getTileLayout(pinnedTiles.length, containerWidth)
                  return (
                    <div
                      ref={gridContainerRef}
                      className="flex-1 min-h-0 grid gap-3 justify-center overflow-y-auto"
                      style={{
                        gridTemplateColumns: `repeat(${layout.cols}, minmax(0, 1fr))`,
                        gridAutoRows: `minmax(${MIN_TILE_HEIGHT}px, 1fr)`,
                      }}
                    >
                      {pinnedTiles.map((p) => (
                        <VoiceTile
                          key={p.userId}
                          {...tileMenuCtx}
                          participant={p}
                          isSelf={p.isSelf}
                          stream={getStream(p)}
                          layoutClass="w-full h-full"
                          cols={0}
                          noAspect
                          onClick={() => setFocusedUserId(p.userId)}
                          connectionState={getConnectionState(p)}
                        />
                      ))}
                    </div>
                  )
                })()}
              </>
            ) : nonScreenParticipants.length === 1 ? (
              <div ref={gridContainerRef} className="flex-1 min-h-0">
                <VoiceTile
                  {...tileMenuCtx}
                  participant={nonScreenParticipants[0]}
                  isSelf={nonScreenParticipants[0].isSelf}
                  stream={getStream(nonScreenParticipants[0])}
                  layoutClass="w-full h-full"
                  cols={0}
                  noAspect
                  onClick={() => setFocusedUserId(nonScreenParticipants[0].userId)}
                  connectionState={getConnectionState(nonScreenParticipants[0])}
                />
              </div>
            ) : (
              (() => {
                // Uniform grid: explicit row+col tracks in fr units make every
                // cell the same size regardless of which tiles have video and
                // which show only an avatar. The previous version used
                // `aspect-video` per tile + `minmax(360px, 600px)` columns,
                // which let 16:9-locked heights overflow into the next row
                // once participants exceeded what comfortably fit, producing
                // visual overlap.
                const layout = getTileLayout(nonScreenParticipants.length, containerWidth)
                return (
                  <div
                    ref={gridContainerRef}
                    // w-full forces the grid to span the full chat
                    // column width — `justify-center` on the parent
                    // flex centred the grid only when its intrinsic
                    // size was less than the parent's, which happens
                    // any time the tile cells settle below the
                    // available row width (e.g. min-height of a 16:9
                    // cell limits how wide cols expand). Result was
                    // 4 tiles huddled centred with horizontal slack on
                    // both sides instead of filling the row.
                    className="flex-1 w-full min-h-0 grid gap-3 overflow-y-auto"
                    style={{
                      gridTemplateColumns: `repeat(${layout.cols}, minmax(0, 1fr))`,
                      gridAutoRows: `minmax(${MIN_TILE_HEIGHT}px, 1fr)`,
                    }}
                  >
                    {nonScreenParticipants.map((p) => (
                      <VoiceTile
                        key={p.userId}
                        {...tileMenuCtx}
                        participant={p}
                        isSelf={p.isSelf}
                        stream={getStream(p)}
                        layoutClass="w-full h-full"
                        cols={0}
                        noAspect
                        onClick={() => setFocusedUserId(p.userId)}
                        connectionState={getConnectionState(p)}
                      />
                    ))}
                  </div>
                )
              })()
            )}
            {/* Transcription rendered inside the main tile area — same as
                speaker mode — so it inherits the outer p-4 and lines up
                flush with the tile above instead of double-padding. */}
            {isInChannel && (
              <ResizableSidePanel channelId={channelId || ''} />
            )}
          </div>
        )}
      </div>

      {/* Sticky ownership (2026-07-07): view-only banner shown in place of the
          normal voice control bar when THIS channel's voice is held on another
          device. The participant grid / chat above stays mounted (view-only).
          "Move here" moves voice to this device after an explicit confirm. */}
      {voiceActiveElsewhere && voiceActiveElsewhere.channelId === channelId && (
        <div className="h-14 shrink-0 px-6 flex items-center justify-between gap-3 border-t border-border bg-card">
          <span className="text-sm text-muted-foreground flex items-center gap-2">
            <Volume2 className="w-4 h-4 text-primary" />
            You're in this channel on another device ({voiceActiveElsewhere.deviceLabel})
          </span>
          <button
            onClick={handleMoveHere}
            className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm font-medium transition duration-150 hover:opacity-90"
          >
            Move here
          </button>
        </div>
      )}
    </div>
  )
}

function PhoneVoiceTile({
  identity,
  layoutClass,
  onClick,
}: {
  identity: string
  layoutClass: string
  onClick?: () => void
}) {
  const e164 = identity.replace(/^phone:|^sip_/, '')
  return (
    <div
      className={`${layoutClass} flex flex-col items-center justify-center gap-2 rounded-xl bg-card border border-border p-4 cursor-default`}
      onClick={onClick}
    >
      <div className="rounded-full bg-primary/15 text-primary p-3">
        <Phone className="h-6 w-6" />
      </div>
      <div className="text-sm font-medium text-foreground">{formatPhoneDisplay(e164)}</div>
      <div className="text-xs text-muted-foreground">Phone caller</div>
    </div>
  )
}

// OSPROD-88: thin dispatcher — phone participants render `PhoneVoiceTile`,
// everyone else falls through to the full `NormalVoiceTile`. Keeps
// hooks unconditional inside each branch and avoids early-return-
// before-hooks in `NormalVoiceTile`.
type VoiceTileProps = {
  participant: any
  isSelf: boolean
  stream: MediaStream | null
  layoutClass: string
  cols?: number
  onClick?: () => void
  connectionState?: { label: string; color: string; icon?: React.ReactNode }
  fit?: 'cover' | 'contain'
  noAspect?: boolean
  // Decode-budget: the tile the user is actually looking at (the focused tile
  // or the speaker-view spotlight). On a busy call it keeps its size-based high
  // layer while every other tile is pinned to 180p. Default false → grid /
  // strip / lobby tiles. See useTileVideoDimensions + services/videoQuality.
  isHero?: boolean
  // Moderation context-menu wiring (Unit F1). Threaded from
  // VoiceChannelView so the per-tile kebab / right-click can open the
  // shared participant menu without each tile re-fetching host status.
  channelId?: string
  serverId?: string | null
  isHost?: boolean
  voiceChannels?: { id: string; name: string }[]
}

// OSPROD-196: shared 1s clock so N participant tiles share ONE timer/tick
// instead of each running its own setInterval (the per-second resolution the
// user asked for, without N intervals re-rendering independently).
let roomClockNow = Date.now()
const roomClockSubs = new Set<() => void>()
let roomClockTimer: ReturnType<typeof setInterval> | null = null
function useRoomClock(): number {
  const [now, setNow] = useState(() => roomClockNow)
  useEffect(() => {
    const cb = () => setNow(roomClockNow)
    roomClockSubs.add(cb)
    if (!roomClockTimer) {
      roomClockTimer = setInterval(() => {
        roomClockNow = Date.now()
        roomClockSubs.forEach((f) => f())
      }, 1000)
    }
    return () => {
      roomClockSubs.delete(cb)
      if (roomClockSubs.size === 0 && roomClockTimer) {
        clearInterval(roomClockTimer)
        roomClockTimer = null
      }
    }
  }, [])
  return now
}

// OSPROD-196: live "time in channel" for a participant, with seconds. Accepts
// a number (epoch ms) OR an ISO string — the server sends joinedAt as an ISO
// string in the participant snapshot, and the old code did numeric math on it
// (`now - joinedAt`) which yielded NaN for everyone but the self tile (whose
// numeric myJoinedAt fallback happened to work) → "only my time showed".
function RoomTime({ joinedAt }: { joinedAt: number | string | null | undefined }) {
  const now = useRoomClock()
  if (joinedAt == null) return null
  const start = typeof joinedAt === 'string' ? Date.parse(joinedAt) : joinedAt
  if (!start || Number.isNaN(start)) return null
  const sec = Math.max(0, Math.floor((now - start) / 1000))
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = sec % 60
  const ss = String(s).padStart(2, '0')
  const label = h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`
  return (
    <span className="text-white/60 text-[10px] tabular-nums truncate" title="Time in channel">
      {label}
    </span>
  )
}

function VoiceTile(props: VoiceTileProps) {
  const isPhone = typeof props.participant.userId === 'string' && (props.participant.userId.startsWith('phone:') || props.participant.userId.startsWith('sip_'))
  if (isPhone) {
    return (
      <PhoneVoiceTile
        identity={String(props.participant.userId)}
        layoutClass={props.layoutClass}
        onClick={props.onClick}
      />
    )
  }
  return <NormalVoiceTile {...props} />
}

function NormalVoiceTile({
  participant,
  isSelf,
  stream,
  layoutClass,
  cols = 2,
  onClick,
  connectionState,
  fit = 'cover',
  noAspect = false,
  channelId,
  serverId,
  isHost = false,
  voiceChannels,
  isHero = false,
}: VoiceTileProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  // OSPROD-208: the self camera tile binds the LiveKit LocalVideoTrack directly
  // (track.attach below) instead of a snapshot MediaStream, so an in-place
  // camera processor swap (background blur / virtual bg → setProcessor/
  // replaceTrack) can't strand the <video> on a now-ended track. localVideoTrack
  // is the live LocalVideoTrack object; LiveKit re-attaches our element to its
  // current track on every swap. Only for the self CAMERA tile — screen-share
  // self tiles and remote tiles keep the snapshot path.
  const localVideoTrack = useLivekitStore((s) => s.localVideoTrack)
  const selfPreviewTrack = isSelf && !participant.isScreenShareOnly ? localVideoTrack : undefined
  // Container around the <video> — measured by the resize observer so
  // we can tell the SFU which spatial layer to forward. Drives the
  // "fullscreen this peer → upgrade to 1080p" behaviour.
  const tileContainerRef = useRef<HTMLDivElement>(null)
  const [videoReady, setVideoReady] = useState(false)
  // Per-tile volume state removed 1.1.21A — master volume on the
  // channel controls bar replaces the inline slider.
  const peerVideoHidden = useVoiceStore((s) => s.peerVideoHidden)
  const selfMirror = useVoiceStore((s) => s.selfMirror)
  const peerVideoToggleInFlight = useVoiceStore((s) => s.peerVideoToggleInFlight)
  const togglePeerVideoHidden = useVoiceStore((s) => s.togglePeerVideoHidden)
  const videoSpeakersOnly = useVoiceStore((s) => s.videoSpeakersOnly)
  const setVideoSpeakersOnly = useVoiceStore((s) => s.setVideoSpeakersOnly)
  const isPeerVideoTogglePending = peerVideoToggleInFlight.has(participant.userId)
  const isHandRaised = useVoiceStore((s) => s.raisedHands.has(participant.userId))
  // OSPROD-61: hourglass overlay when the participant has flagged "Be
  // right back". Same lookup model as raisedHands.
  const isAway = useVoiceStore((s) => s.awayParticipants.has(participant.userId))
  const tileReactions = useVoiceStore((s) => s.participantReactions[participant.userId])
  // F3: emotional-rating badge. The batch fetch runs in VoiceChannelView;
  // here we just read from the store. Strip '-screen' so a screen-share tile
  // resolves to the same person rating as the camera tile.
  const _emotionRatingUserId = String(participant.userId ?? '').replace('-screen', '')
  const emotionRating = useEmotionRatingStore((s) => s.byUserId[_emotionRatingUserId])
  const incomingMode = useVoiceStore((s) => s.incomingQuality)
  // OSPROD-196: fallback for the self tile, whose participant row may not carry
  // joinedAt until the presence snapshot round-trips.
  const myJoinedAt = useVoiceStore((s) => s.joinedAt)
  const viewMode = useVoiceStore((s) => s.viewMode)
  const setPeerVideoOffscreen = useVoiceStore((s) => s.setPeerVideoOffscreen)
  // Off-screen video pause only engages on big calls — boolean selector so it
  // flips only when the participant count crosses the threshold (not on every
  // join/leave), keeping the effect stable.
  const offscreenPauseThreshold = offscreenPauseMinParticipants()
  const isLargeCall = useVoiceStore((s) => s.participants.length >= offscreenPauseThreshold)
  // OSPROD-64: read pin state for THIS tile + cap so the button can be
  // disabled when pinning further would violate N-1.
  const isPinned = useVoiceStore((s) => s.pinnedUserIds.has(participant.userId))
  const togglePin = useVoiceStore((s) => s.togglePin)
  const pinCapReached = useVoiceStore((s) => {
    const N = s.participants.length
    return s.pinnedUserIds.size >= Math.max(0, N - 1)
  })

  // Unit F1: open the shared participant moderation menu (Unit D store)
  // from this tile — via right-click on the tile or the ⋮ kebab. Self
  // and screen-share-only tiles never open it. The identity is the
  // LiveKit identity with any '-screen' suffix stripped; guest tiles
  // (identity `guest_*`) carry no DB user, so `userId` is left undefined
  // and `subjectType` is GUEST. Phone tiles render in PhoneVoiceTile and
  // never reach here.
  const openMenu = useParticipantActionsStore((s) => s.openMenu)
  const openTileMenu = (e: React.MouseEvent) => {
    if (isSelf || participant.isScreenShareOnly) return
    e.preventDefault()
    const identity = String(participant.userId).replace(/-screen$/, '')
    const isGuest = participant.isGuest || identity.startsWith('guest_')
    openMenu(
      {
        userId: isGuest ? undefined : identity,
        identity,
        subjectType: isGuest ? 'GUEST' : 'USER',
        displayName: getUserDisplayName(participant.user) || participant.user?.username || 'User',
        avatarUrl: participant.user?.avatarUrl ?? null,
      },
      { channelId: channelId ?? '', serverId: serverId ?? null, isHost, voiceChannels },
      e.clientX,
      e.clientY,
    )
  }

  // Look up the underlying LiveKit Participant so we can poll its RTC
  // stats. For the self-tile that's room.localParticipant; for remotes
  // we match by identity (= our userId in the merged shape).
  const livekitRoomForStats = useLivekitStore((s) => s.room)
  const livekitParticipant = isSelf
    ? livekitRoomForStats?.localParticipant
    : livekitRoomForStats?.remoteParticipants.get(participant.userId)
  const bandwidthKbps = useParticipantBandwidth(livekitParticipant ?? null, isSelf)
  // Self-tile shows BOTH up + down: outbound is what THIS browser
  // sends to the SFU (own mic/cam/screen), inbound is the aggregate
  // pulled down for every other participant's tracks. The remote-tile
  // path stays on the per-participant hook above (single chip).
  const selfBandwidth = useSelfBandwidth(isSelf ? livekitRoomForStats : null)
  // Drive the SFU's spatial-layer choice from the tile's actual
  // rendered size — only for remote tiles (the self-tile reads its
  // local track directly), and only when the user is on the 'auto'
  // incoming preset. A user with manual MEDIUM/LOW cap doesn't want
  // a fullscreened tile to silently demand 8 Mbps.
  const bigRoomForcedLow = useLivekitStore((s) => s.bigRoomForcedLow)
  useTileVideoDimensions({
    room: livekitRoomForStats,
    participantId: isSelf ? null : String(participant.userId).replace('-screen', ''),
    source: participant.isScreenShareOnly ? 'screen' : 'camera',
    incomingMode,
    bigRoomForcedLow,
    isHero,
    elementRef: tileContainerRef,
  })

  // Pause the SFU video subscription for a remote camera tile that scrolls
  // off-screen in the GRID → stop decoding it. On a big call this is the
  // difference between decoding the ~6-12 visible tiles and all 20+ — the
  // 2026-05-22 camera-flicker root cause was ~24 simultaneous VP9 decodes
  // saturating the CPU (telemetry: inbound freezes 85-191/peer; all already
  // at 180p, so the lever is COUNT, not bitrate). Reuses the
  // peerVideoHidden→setSubscribed path (VoiceManager). GRID-ONLY: speaker view
  // shows few videos AND can render one uid twice (spotlight + strip), which
  // would flap a uid-keyed map. Self + screen-share tiles exempt. BIG CALLS
  // ONLY (isLargeCall ≥ offscreenPauseMinParticipants, default 14): on
  // small/medium calls every machine decodes all tiles fine, so pausing would
  // only add a re-subscribe blink when you scroll, for no benefit.
  // Kill-switch: localStorage 'voicePauseOffscreenVideo'='false' (rejoin).
  useEffect(() => {
    if (isSelf || participant.isScreenShareOnly) return
    if (!isLargeCall) return // small/medium call → keep every tile subscribed
    if (viewMode !== 'grid') return
    if (typeof IntersectionObserver === 'undefined') return
    if (typeof localStorage !== 'undefined' && localStorage.getItem('voicePauseOffscreenVideo') === 'false') return
    const el = tileContainerRef.current
    if (!el) return
    const uid = String(participant.userId).replace('-screen', '')
    let offTimer: ReturnType<typeof setTimeout> | null = null
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((e) => e.isIntersecting)
        if (visible) {
          if (offTimer) { clearTimeout(offTimer); offTimer = null }
          setPeerVideoOffscreen(uid, false) // re-subscribe immediately
        } else {
          if (offTimer) clearTimeout(offTimer)
          offTimer = setTimeout(() => setPeerVideoOffscreen(uid, true), OFFSCREEN_PAUSE_DEBOUNCE_MS)
        }
      },
      // Generous margin: subscribe a tile just before it enters view, and only
      // pause once it's well off-screen — avoids black tiles at the fold.
      { rootMargin: '400px 0px' },
    )
    obs.observe(el)
    return () => {
      if (offTimer) clearTimeout(offTimer)
      obs.disconnect()
      // Never leave a peer stuck unsubscribed because their tile unmounted
      // (view switch / participant left) — default back to subscribed.
      setPeerVideoOffscreen(uid, false)
    }
  }, [isSelf, participant.isScreenShareOnly, participant.userId, viewMode, isLargeCall, setPeerVideoOffscreen])
  // Self-tile reads its own audio level straight from LiveKit; the
  // stream-based meter struggles with publish/unpublish cycles when the
  // user toggles mute.
  const selfAudioLevel = useLocalAudioLevel()

  // Live "speaking now" flag driven from raw audioLevel polled every
  // 150ms. Was using `participant.isSpeaking` which is set from LiveKit's
  // RoomEvent.IsSpeakingChanged — that fires on threshold transitions
  // with ~500ms hysteresis baked in by the SDK, so the green border
  // would only appear after someone had been talking a beat too long
  // and the user couldn't tell who was speaking. Direct audioLevel poll
  // matches the speaker-promotion algorithm's THRESHOLD = 0.05 and
  // gives instant feedback.
  const livekitRoomForLevel = useLivekitStore((s) => s.room)
  const [speakingNow, setSpeakingNow] = useState(false)
  useEffect(() => {
    if (!livekitRoomForLevel) {
      setSpeakingNow(false)
      return
    }
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    const tick = () => {
      if (cancelled) return
      let level = 0
      if (isSelf) {
        level = livekitRoomForLevel.localParticipant.audioLevel || 0
      } else {
        const remote = livekitRoomForLevel.remoteParticipants.get(
          String(participant.userId).replace('-screen', '')
        )
        level = remote?.audioLevel || 0
      }
      const speaking = level > 0.05
      setSpeakingNow((prev) => (prev !== speaking ? speaking : prev))
      timer = setTimeout(tick, 150)
    }
    tick()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [livekitRoomForLevel, isSelf, participant.userId])

  const isTileVideoHidden = !!peerVideoHidden[participant.userId]

  // Analyze stream tracks
  const videoTracks = stream?.getVideoTracks() || []
  const audioTracks = stream?.getAudioTracks() || []
  const hasVideoTrack = videoTracks.length > 0
  const hasAudioTrack = audioTracks.length > 0
  // Hold the last frame through a transient freeze instead of flashing the
  // avatar (the desktop "мерцание камеры со спинером"). A frozen INCOMING track
  // fires `muted` only on Electron, so we deliberately do NOT filter muted
  // tracks here — only ENDED ones. Full rationale + prod telemetry live in
  // hasRenderableVideo(). (audioActive keeps its `!t.muted` check: the
  // "Audio paused" badge is a deliberate, non-flickering indicator and mic-mute
  // semantics are unrelated.)
  // Self camera: gate on the LIVE LocalVideoTrack getter (not the cached
  // snapshot, which goes stale/ended after an in-place processor swap —
  // OSPROD-208). Remote/screen-share tiles keep the freeze-tolerant snapshot
  // path (hold the last frame through a transient muted/frozen track).
  const videoActive = selfPreviewTrack
    ? isSelfCameraRenderable(selfPreviewTrack, !!participant.isVideoOn)
    : hasRenderableVideo(videoTracks)
  const audioActive = audioTracks.some(t => !t.muted && t.readyState !== 'ended')

  // OSPROD-123: dropped the OSPROD-119 dim/desaturate filter for muted
  // participants — the visual was reading as "their video is broken" /
  // "user is afk", and the user wants every tile to look the same
  // regardless of mute state, with the speaking ring being the only
  // active-state indicator. Mute is still surfaced via the red mic-off
  // icon at top-left (kept fully saturated). `isMutedAppearance` is
  // preserved as a soft ring color cue (muted = lighter ring) so a
  // glance at the grid still distinguishes mic states without dimming
  // the video itself.
  const isMutedAppearance = !!participant.isMuted && !participant.isScreenShareOnly

  // Mount video element if we have ANY stream (to play audio too), but only show it visually when video is active
  const hasStream = !!stream
  // For the self camera tile, visibility is driven entirely by the live track +
  // isCameraEnabled (videoActive already folds both in). Remote/screen-share
  // tiles keep the snapshot-based gate. OSPROD-208.
  const shouldShowVideo = selfPreviewTrack
    ? videoActive
    : stream && hasVideoTrack && videoActive && (
        participant.isScreenShareOnly || participant.isVideoOn
      )
  const isVideoActuallyPlaying = shouldShowVideo && videoReady && !isTileVideoHidden

  // OSPROD-208: bind the self camera preview to the LiveKit LocalVideoTrack.
  // LiveKit re-attaches our <video> to the live track on every in-place swap
  // (replaceTrack / setProcessor / stopProcessor / restart), so the element
  // follows background-blur / virtual-bg changes instead of stranding on the
  // ended canvas track the way a manual srcObject snapshot did.
  useEffect(() => {
    const el = videoRef.current
    if (!el || !selfPreviewTrack) return
    // CRITICAL (self-monitoring / "слышу себя"): attachSelfPreview clears
    // srcObject before attach() and hard-asserts muted, because LiveKit's
    // attachToElement REUSES the existing srcObject (which the snapshot path may
    // have populated with the local MIC track) and would otherwise set
    // muted=false → the user hears their own mic on camera-on. See #69.
    attachSelfPreview(el, selfPreviewTrack)
    return () => {
      try { selfPreviewTrack.detach(el) } catch { /* noop */ }
    }
  }, [selfPreviewTrack])

  useEffect(() => {
    // The self camera tile is owned by the attach() effect above — don't fight
    // it by writing srcObject from the (snapshot) stream.
    if (selfPreviewTrack) return
    if (videoRef.current && stream) {
      const current = videoRef.current.srcObject as MediaStream | null
      const currentIds = current?.getTracks().map((t) => t.id).sort().join(',') || ''
      const newIds = stream.getTracks().map((t) => t.id).sort().join(',')
      if (currentIds !== newIds) {
        videoRef.current.srcObject = stream
        // Do NOT blank videoReady here. A re-subscribe (off-screen pause,
        // simulcast/layer switch, brief stall→re-attach) swaps srcObject for the
        // SAME peer; flipping videoReady→false would blank the tile to grey =
        // the "обрыв картинки" we're killing. Hold the last frame; onPlay keeps
        // videoReady true. A truly different participant remounts the component
        // (React key=userId) so the initial useState(false) still covers a new tile.
      }
    }
  }, [stream, selfPreviewTrack])

  // The inline `<video muted={true}>` element doesn't play audio
  // anyway — all peer audio flows through LiveKitBackgroundAudio's
  // Web Audio graph (per-peer GainNode × masterVolume). No tile-side
  // volume effect needed after the per-tile slider was removed.

  const gapRem = 0.75 // gap-3
  const basis = cols > 0 ? `calc(${100 / cols}% - ${((cols - 1) * gapRem) / cols}rem)` : undefined

  return (
    <div
      onClick={onClick}
      onContextMenu={openTileMenu}
      // OSPROD-254: `isolate` gives the tile its own stacking context so the
      // internal z-10/z-20 overlay layers can never interleave with content
      // outside the tile (main-container text bleeding into thumbnails).
      // `[container-type:size]` makes the tile a CSS size-container so the
      // secondary overlays below can hide themselves via @container queries
      // when the tile is thumbnail-sized (top strips) — at any participant
      // count the tile stays self-contained instead of overflowing/cropping.
      className={`group relative isolate [container-type:size] flex items-center justify-center rounded-xl bg-card ring-1 ${isMutedAppearance ? 'ring-muted-foreground/25 shadow-none' : 'ring-border shadow-md'} ${layoutClass} ${noAspect ? '' : 'aspect-video'} cursor-pointer overflow-hidden`}
      style={{
        ...(basis ? { flex: `0 0 ${basis}` } : {}),
        // The previous attempt used hsl(var(--card)) which never resolved
        // because the design tokens are oklch and named --color-card.
        // Falling back to Tailwind's bg-card + ring-border is theme-aware
        // and gives the tile an actual visible boundary in both modes.
        // The speaking ring is overlaid so it doesn't fight the base
        // border.
        // OSPROD-123: speaking indicator moved from `boxShadow` on the
        // outer container (which got painted over by the video element
        // inside the absolute-positioned inner wrapper) to a dedicated
        // overlay <div> just below — an absolute, `pointer-events:none`,
        // `inset-0`, ring-styled layer that sits ON TOP of the video.
        // Outer container keeps only a soft glow + the transition so a
        // tile that flips into/out of speaking still has a smooth edge.
        boxShadow: speakingNow
          ? '0 0 18px 2px rgba(34, 197, 94, 0.45)'
          : undefined,
        transition: 'box-shadow 0.12s ease, opacity 0.2s ease',
      }}
    >
      {/* Clipped inner area to keep video/avatar within rounded corners.
          OSPROD-123: dropped the OSPROD-119 mute filter — every tile now
          renders at full saturation regardless of mute state; the red
          mic-off badge is the sole mute indicator. */}
      <div
        ref={tileContainerRef}
        className="absolute inset-0 rounded-xl overflow-hidden"
      >
        {/* Video element — always mount if stream exists to play audio AND video.
            OSPROD-208: also mount for the self camera tile (selfPreviewTrack)
            so the attach() effect always has an element to bind, even if the
            snapshot stream is momentarily null. */}
        {(hasStream || !!selfPreviewTrack) && (
          <video
            ref={videoRef}
            autoPlay
            muted={true}
            playsInline
            // Mirror the LOCAL camera preview only — Zoom/Meet/Telegram
            // all do this so the user moves left/right naturally on
            // screen ("looking in a mirror"). Skip mirroring on screen
            // shares (text would render reversed) and on remote streams
            // (we want to see them as they are). The other side still
            // receives the un-mirrored stream — this is purely the local
            // <video> element's display transform.
            className={`absolute inset-0 w-full h-full object-${fit} ${isVideoActuallyPlaying ? 'opacity-100' : 'opacity-0 pointer-events-none'} ${isSelf && !participant.isScreenShareOnly && selfMirror ? '-scale-x-100' : ''}`}
            onLoadedMetadata={() => setVideoReady(true)}
            onPlay={() => setVideoReady(true)}
            // NOTE: intentionally NOT flipping videoReady=false on `waiting`/`stalled`.
            // A transient stall (a remote peer's machine choking → their stream
            // pauses a few seconds) used to hide the <video> (opacity-0) and pop
            // the loading spinner over a grey tile — which read as constant
            // "flicker" on the desktop (Electron) app while plain browsers just
            // held the last frame. We now match the browser: keep the last frame
            // painted through a stall, no spinner. videoReady still resets on a
            // genuine srcObject/track change (effect above) and the avatar still
            // shows when the camera is actually off (shouldShowVideo=false), so
            // real "no video" states are unaffected. (2026-06-19 desktop-flicker incident.)
          />
        )}

        {/* Camera-loading spinner REMOVED (2026-06-19). On a busy call a remote
            tile's video momentarily isn't "playing" on every re-subscribe /
            simulcast-layer switch / brief stall; the spinner-over-grey popped on
            each of those = a noticeable "обрыв картинки" (worse on the Electron
            desktop app). We now show nothing extra: the last frame is held (the
            srcObject effect no longer blanks videoReady) and the avatar fallback
            below covers a genuinely-off camera. No loading crutch. */}

        {/* Fallback avatar when video is not playing. Avatar + text
            scale via container-query units (`cqmin` = % of the tile's
            smaller dimension), so the same component looks right
            everywhere: tiny in a 260-px-wide screen-share strip,
            big on a focused full-area tile. clamp() bounds it so the
            avatar never gets uselessly small or absurdly large. */}
        {(!isVideoActuallyPlaying || !shouldShowVideo) && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-3 text-center [container-type:size]">
            <div className="relative shrink-0" style={{ width: 'clamp(2.75rem, 32cqmin, 13rem)' }}>
              <div
                className="rounded-full overflow-hidden ring-4 ring-white/10 w-full"
                style={{ aspectRatio: '1' }}
              >
                {participant.user?.avatarUrl ? (
                    <img
                      src={mediaUrl(participant.user.avatarUrl)}
                      alt=""
                      className="w-full h-full object-cover"
                      // If the avatar URL 404s (server down, image deleted),
                      // hide the broken-image and reveal the initial fallback
                      // beneath. Without this, users with a stale avatarUrl
                      // see an empty square instead of their first letter.
                      onError={(e) => {
                        const img = e.currentTarget
                        img.style.display = 'none'
                        const next = img.nextElementSibling as HTMLElement | null
                        if (next) next.style.display = 'flex'
                      }}
                    />
                  ) : null}
                <div
                  className={`w-full h-full bg-primary items-center justify-center text-primary-foreground font-bold ${participant.user?.avatarUrl ? 'hidden' : 'flex'}`}
                  style={{ fontSize: 'clamp(0.875rem, 14cqmin, 4rem)' }}
                >
                  {displayInitial(getUserDisplayName(participant.user))}
                </div>
              </div>
              <BirthdayBadge userId={participant.userId} size="md" />
            </div>
            {/* OSPROD-254: in thumbnail-sized tiles (top strips, ≤8.5rem tall)
                the centered name + status text overflowed the tile and got
                cropped mid-string ("…camera button to …" bleeding through the
                thumbnail). Hide them there — the avatar plus the bottom-left
                name tag are enough at that size. */}
            <div
              className="text-foreground font-semibold drop-shadow-sm leading-tight [@container(max-height:8.5rem)]:hidden"
              style={{ fontSize: 'clamp(0.75rem, 5cqmin, 1.5rem)' }}
            >
              {isSelf
                ? `${getUserDisplayName(participant.user) || 'You'} (you)`
                : getUserDisplayName(participant.user)}
            </div>
            {(!isSelf && isTileVideoHidden) ? (
              // You HID this person's video (peerVideoHidden) — show the reason
              // + a Show button instead of the misleading "Camera off" (their
              // camera may be on; you just hid it and may have forgotten). This
              // is the "это не зависание, а ваша настройка" marker.
              <div
                className="flex items-center gap-2 rounded-full bg-black/55 px-3 py-1.5 ring-1 ring-white/10 [@container(max-height:8.5rem)]:hidden"
                style={{ fontSize: 'clamp(0.625rem, 2.6cqmin, 0.95rem)' }}
              >
                <VideoOff size={14} className="text-destructive shrink-0" />
                <span className="text-foreground/90">You hid their video</span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); if (!isPeerVideoTogglePending) togglePeerVideoHidden(participant.userId) }}
                  disabled={isPeerVideoTogglePending}
                  aria-busy={isPeerVideoTogglePending || undefined}
                  className="rounded-full bg-primary px-2.5 py-1 text-primary-foreground font-medium hover:opacity-90 transition disabled:opacity-60 disabled:cursor-wait"
                >
                  {isPeerVideoTogglePending ? 'Showing…' : 'Show'}
                </button>
              </div>
            ) : (!isSelf && !participant.isScreenShareOnly && videoSpeakersOnly && participant.isVideoOn) ? (
              // "Видео только говорящих" (videoSpeakersOnly) unsubscribed this
              // camera — the peer IS publishing (roster isVideoOn true), this
              // client just isn't receiving it. Without this marker the tile
              // fell through to "Camera off", which reads as a platform video
              // outage (2026-07-20: a forgotten toggle was reported as "видео
              // сломалось после апдейтов"). Mirrors the peerVideoHidden marker
              // above; the button flips the mode off and VoiceManager's store
              // subscription re-subscribes every camera immediately.
              <div
                className="flex items-center gap-2 rounded-full bg-black/55 px-3 py-1.5 ring-1 ring-white/10 [@container(max-height:8.5rem)]:hidden"
                style={{ fontSize: 'clamp(0.625rem, 2.6cqmin, 0.95rem)' }}
              >
                <Focus size={14} className="text-primary shrink-0" />
                <span className="text-foreground/90">Speakers-only video is on</span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setVideoSpeakersOnly(false) }}
                  className="rounded-full bg-primary px-2.5 py-1 text-primary-foreground font-medium hover:opacity-90 transition"
                >
                  Show all
                </button>
              </div>
            ) : (
              <div
                className="text-muted-foreground leading-snug whitespace-normal [@container(max-height:8.5rem)]:hidden"
                style={{
                  fontSize: 'clamp(0.625rem, 2.8cqmin, 1rem)',
                  maxWidth: 'min(95%, 26rem)',
                }}
              >
                {isSelf
                  ? participant.isVideoOn
                    ? 'Camera on'
                    : 'Camera off — click the camera button to enable video'
                  : 'Camera off'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* OSPROD-123: speaking ring overlay. Sits ABOVE the video element
          (which lives inside tileContainerRef) with pointer-events:none
          so clicks fall through to the tile. Inset ring so the 3 px
          stroke stays inside the rounded corners — and on top of the
          video, unlike the prior boxShadow-on-outer-container approach
          which got painted over by the video layer. */}
      {/* OSPROD-263: raised-hand ring — same construction as the speaking
          ring below, in the same yellow as the raise-hand icon/badge
          (yellow-500). Persists until the hand is lowered. Rendered BEFORE
          the speaking ring so live speech still reads green on top. */}
      {isHandRaised && !participant.isScreenShareOnly && (
        <div
          aria-hidden
          className="absolute inset-0 rounded-xl ring-[3px] ring-inset ring-yellow-500 pointer-events-none z-20"
        />
      )}
      {speakingNow && (
        <div
          aria-hidden
          className="absolute inset-0 rounded-xl ring-[3px] ring-inset ring-green-500 pointer-events-none z-20 transition-opacity duration-100"
        />
      )}

      {/* Name tag — Warmly-style: real name + position line. Position only
          renders for non-screen tiles and only if the user has filled it
          in (User.position is optional). Falls back to "You"/username when
          firstName isn't set. */}
      <div className="absolute bottom-3 left-3 flex items-start gap-1.5 bg-black/60 backdrop-blur-sm px-2 py-1 rounded-md max-w-[calc(100%-1.5rem)]">
        {/* Always render the meter so the user has visual feedback whenever
            they speak. For self we read LiveKit's own audioLevel — the
            stream-based path is unreliable across publish/unpublish.
            Remote tiles keep using the stream; LiveKit drives audioLevel
            via active-speaker events for them too but we already have a
            MediaStream pipeline working there. */}
        <AudioMeter
          stream={stream}
          size="sm"
          bars={4}
          audioLevel={isSelf ? (participant.isScreenShareOnly ? 0 : selfAudioLevel) : undefined}
        />
        <div className="flex flex-col leading-tight min-w-0">
          <span className="text-white text-xs font-medium truncate">
            {participant.isScreenShareOnly
              ? `${getUserDisplayName(participant.user) || (isSelf ? 'You' : participant.user?.username)} (screen)`
              : (getUserDisplayName(participant.user) || (isSelf ? 'You' : participant.user?.username))}
          </span>
          {/* OSPROD-254: in thumbnail-sized tiles the 3-line tag (name +
              position + time) covered most of the tile and overlapped the
              centered avatar. Keep only the name line there. */}
          {!participant.isScreenShareOnly && participant.user?.position && (
            <span className="text-white/70 text-[10px] truncate [@container(max-height:8.5rem)]:hidden">
              {participant.user.position}
            </span>
          )}
          {!participant.isScreenShareOnly && (
            <span className="[@container(max-height:8.5rem)]:hidden">
              <RoomTime joinedAt={participant.joinedAt ?? (isSelf ? myJoinedAt : null)} />
            </span>
          )}
        </div>
        {participant.isGuest && (
          <span className="text-[10px] bg-primary/80 text-white px-1.5 py-0.5 rounded-full font-medium self-center">
            Guest
          </span>
        )}
        {/* F3: emotional-rating pill — lives INSIDE the name tag (a person
            attribute), so it never collides with the corner overlays (mute
            stack top-left, platform/bandwidth panel bottom-right). Visible to
            authorized viewers only: the batch fetch populates the store;
            unauthorized viewers / guests get no entry → pill hidden. */}
        {typeof emotionRating === 'number' && (
          <span
            className={`self-center shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-white ${
              emotionRating < 40 ? 'bg-destructive' : emotionRating <= 60 ? 'bg-amber-500' : 'bg-emerald-500'
            }`}
            title="Emotional rating"
          >
            {emotionRating}
          </span>
        )}
      </div>

      {/* Remote participant controls (hide video only — per-tile volume
          slider removed 2026-05-16 per user feedback: it broke whenever
          participants reordered and the binding aliased between tiles,
          and trying to coax a per-peer level via the small inline UI
          was the wrong abstraction. Master volume now lives on the
          channel-level VoiceChannelControls bar at the bottom of the
          room so it applies to the whole call. Per-peer mute can be
          rebuilt later via the participant context menu if QA asks).
          OSPROD-86: skip on screen-share-only tiles — they have no
          audio track, "hide video" would hide the share itself, and
          the buttons collided with the fullscreen icon on the
          ScreenShareAnnotation wrapper (top-2 right-2 vs top-3 right-3). */}
      {!isSelf && !participant.isScreenShareOnly && (
        <div className="absolute top-2 right-2 flex items-center gap-2 z-10">
          {/* Hide video toggle (OSPROD-57: disabled+spinner while
              setSubscribed() is in flight, so rapid clicks can't pile
              up subscribe/unsubscribe requests at the SFU). */}
          <button
            onClick={(e) => {
              e.stopPropagation()
              if (!isPeerVideoTogglePending) togglePeerVideoHidden(participant.userId)
            }}
            disabled={isPeerVideoTogglePending}
            aria-busy={isPeerVideoTogglePending || undefined}
            title={isPeerVideoTogglePending ? 'Updating…' : isTileVideoHidden ? 'Show video' : 'Hide video'}
            className={`w-7 h-7 rounded-md bg-black/60 backdrop-blur-sm flex items-center justify-center transition ${isTileVideoHidden ? 'text-destructive bg-destructive/20' : 'text-white hover:bg-black/80'} ${isPeerVideoTogglePending ? 'opacity-60 cursor-wait' : ''}`}
          >
            {isPeerVideoTogglePending
              ? <Loader2 size={14} className="animate-spin" />
              : isTileVideoHidden ? <VideoOff size={14} /> : <Video size={14} />
            }
          </button>
          {/* Unit F1: ⋮ opens the shared participant moderation menu. */}
          <button
            onClick={(e) => { e.stopPropagation(); openTileMenu(e) }}
            title="More actions"
            className="w-7 h-7 rounded-md bg-black/60 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/80 transition opacity-0 group-hover:opacity-100"
          >
            <MoreVertical size={14} />
          </button>
        </div>
      )}

      {/* Audio / Mute / Deafen status indicators */}
      {!participant.isScreenShareOnly && (
        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          {/* Audio stream indicator */}
          {stream && !hasAudioTrack && !participant.isMuted && (
            <div className="w-7 h-7 rounded-full bg-muted-foreground/90 flex items-center justify-center" title="No audio">
              <MicOff size={14} className="text-white/70" />
            </div>
          )}
          {stream && hasAudioTrack && !audioActive && !participant.isMuted && (
            <div className="w-7 h-7 rounded-full bg-yellow-500/90 flex items-center justify-center" title="Audio paused">
              <Mic size={14} className="text-white" />
            </div>
          )}
          {participant.isMuted && !participant.forceMuted && (
            <div className="w-7 h-7 rounded-full bg-destructive/90 flex items-center justify-center" title="Microphone off">
              <MicOff size={14} className="text-white" />
            </div>
          )}
          {participant.forceMuted && (
            <div className="w-7 h-7 rounded-full bg-destructive flex items-center justify-center ring-2 ring-destructive/40" title="Force-muted by an administrator">
              <span className="relative inline-flex">
                <MicOff size={14} className="text-white" />
                <Lock size={9} className="absolute -right-1 -bottom-1 text-white" />
              </span>
            </div>
          )}
          {participant.isDeafened && (
            <div className="w-7 h-7 rounded-full bg-destructive/90 flex items-center justify-center" title="Audio deafened">
              <HeadphoneOff size={14} className="text-white" />
            </div>
          )}
          {isHandRaised && (
            <div className="w-7 h-7 rounded-full bg-yellow-500/90 flex items-center justify-center animate-pulse" title="Hand raised">
              <Hand size={14} className="text-white" />
            </div>
          )}
          {/* OSPROD-84 follow-up: BRB was previously a stand-alone badge
              at `top-3 right-3` (self) / `top-12 right-3` (remote). The
              self path collided with the right-side Pin button after it
              migrated to top-2 right-2, and the remote path crowded the
              volume slider's open-area. Pulled back into the left
              status stack — same column as mic/deafen/hand, so the
              right side of the tile is dedicated to action controls
              only. The original "collides with the top-2 left-2 pin
              button on self-tile" worry (OSPROD-61 follow-up) no longer
              applies — self-pin lives on the right now. */}
          {isAway && (
            <div className="w-7 h-7 rounded-full bg-zinc-700/90 flex items-center justify-center" title="Be right back">
              <Hourglass size={14} className="text-white" />
            </div>
          )}
        </div>
      )}

      {/* Floating emoji reactions — anchored at the bottom-center of
          the tile and rising via the reactionFloat keyframe. Bursts
          self-prune from voiceStore after ~2.5s. pointer-events-none so
          a flurry of emojis can't swallow tile clicks (volume slider,
          fullscreen). z-20 sits above the video/avatar layer (z-10) and
          below tile-level overlays. */}
      {tileReactions && tileReactions.length > 0 && (
        <div className="absolute inset-x-0 bottom-12 z-20 pointer-events-none">
          {tileReactions.map((r) => (
            <span
              key={r.id}
              className="animate-reaction-float absolute bottom-0 left-1/2 text-4xl drop-shadow-[0_2px_4px_rgba(0,0,0,0.45)] select-none"
              style={{ filter: 'drop-shadow(0 0 6px rgba(0,0,0,0.35))' }}
            >
              {r.emoji}
            </span>
          ))}
        </div>
      )}

      {/* Connection / media state. The connection-state badge is gated
          by `connectionState` (only set for remotes + the self-tile
          while LiveKit is still negotiating). Bandwidth chips render
          for the self-tile unconditionally so the user can see their
          own up/down throughput even when the connection badge has
          dropped off after a successful join. */}
      {(connectionState || isSelf || participant.clientInfo?.label || participant.bandwidthSuppressed) && (
        // OSPROD-254: hidden in thumbnail-sized tiles — the chip stack is
        // taller than the tile itself there and buried the video/avatar.
        <div className="absolute bottom-3 right-3 flex flex-col items-end gap-1 [@container(max-height:8.5rem)]:hidden">
          {/* Bandwidth-suppression chip. Renders on the suppressed
              participant's tile (self + remotes) so the call as a
              whole understands "Vasya's video froze because his
              connection is weak, not because he chose to disable
              his camera". The local user also sees this on their
              own tile as a persistent reminder — the toast that
              fired on suppression scrolls away, this stays. See
              services/connectionQualityWatcher.ts. */}
          {participant.bandwidthSuppressed && (
            <div
              className="bg-amber-500/25 backdrop-blur-sm px-2 py-0.5 rounded-md flex items-center gap-1 text-[10px] font-semibold text-amber-200"
              title={
                isSelf
                  ? 'Слабый интернет — видео и экран отключены, чтобы сохранить голос. Сами включатся, когда сеть стабилизируется.'
                  : `${participant.user.username}: слабая связь, видео скрыто, чтобы голос дошёл до всех. Включится, когда сеть восстановится.`
              }
            >
              <WifiOff size={10} />
              <span>{isSelf ? 'Слабый интернет' : 'Слабая связь у участника'}</span>
            </div>
          )}
          {/* OSPROD-81: client-app badge ("Windows App", "Web / Chrome",
              "Android App", …) — sits above the CONNECTED chip so the two
              read as one network/client status group. Icon is picked by
              client kind: laptop for desktop apps, phone for mobile apps,
              globe for everyone else. A dim sub-line under the label shows
              the web build (appVersion) and, in a native shell, the
              Electron/Capacitor build (nativeBuild) — nested in the same
              chip so the dark backdrop keeps it legible over bright video.
              Tooltip spells out the full text for screen readers +
              overflow-truncated tiles. */}
          {participant.clientInfo?.label && (
            <div
              className="bg-black/60 backdrop-blur-sm px-2 py-0.5 rounded-md flex flex-col items-end gap-0.5"
              title={
                participant.clientInfo.appVersion
                  ? `${participant.clientInfo.label} — web build ${participant.clientInfo.appVersion}${participant.clientInfo.nativeBuild ? ` · app ${participant.clientInfo.nativeBuild}` : ''}`
                  : participant.clientInfo.label
              }
            >
              <div className="flex items-center gap-1 text-[10px] font-medium text-white/85">
                {participant.clientInfo.isElectron
                  ? <Laptop size={10} />
                  : participant.clientInfo.isMobile
                    ? <Smartphone size={10} />
                    : <Globe size={10} />}
                <span>{participant.clientInfo.label}</span>
              </div>
              {participant.clientInfo.appVersion && (
                <span className="text-[9px] leading-none text-white/70 tabular-nums">
                  {participant.clientInfo.appVersion}
                  {participant.clientInfo.nativeBuild ? ` · app ${participant.clientInfo.nativeBuild}` : ''}
                </span>
              )}
            </div>
          )}
          {connectionState && (
            <div className="bg-black/60 backdrop-blur-sm px-2 py-1 rounded-md flex items-center gap-1">
              {connectionState.icon}
              <span className="text-[10px] font-medium uppercase tracking-wider" style={{ color: connectionState.color }}>
                {connectionState.label}
              </span>
            </div>
          )}
          {isSelf ? (
            <>
              {selfBandwidth.outKbps != null && (
                <div
                  className="bg-black/60 backdrop-blur-sm px-2 py-0.5 rounded-md flex items-center gap-1 text-[10px] font-medium tabular-nums text-white/85"
                  title="Upload bitrate to server (your mic / camera / screen)"
                >
                  <ArrowUp size={10} />
                  <span>{formatBandwidth(selfBandwidth.outKbps)}</span>
                </div>
              )}
              {selfBandwidth.inKbps != null && (
                <div
                  className="bg-black/60 backdrop-blur-sm px-2 py-0.5 rounded-md flex items-center gap-1 text-[10px] font-medium tabular-nums text-white/85"
                  title="Download bitrate from server (everyone else's tracks combined)"
                >
                  <ArrowDown size={10} />
                  <span>{formatBandwidth(selfBandwidth.inKbps)}</span>
                </div>
              )}
            </>
          ) : (
            bandwidthKbps != null && (
              <div
                className="bg-black/60 backdrop-blur-sm px-2 py-0.5 rounded-md flex items-center gap-1 text-[10px] font-medium tabular-nums text-white/85"
                title="Download bitrate from this participant"
              >
                <ArrowDown size={10} />
                <span>{formatBandwidth(bandwidthKbps)}</span>
              </div>
            )
          )}
        </div>
      )}
      {!participant.isScreenShareOnly && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            // The store also enforces the cap, but bail here so the
            // tooltip's promise ("Cannot pin all participants…") matches
            // the actual click behaviour for the disabled state too.
            if (!isPinned && pinCapReached) return
            togglePin(participant.userId)
          }}
          disabled={!isPinned && pinCapReached}
          title={
            isPinned
              ? 'Unpin'
              : pinCapReached
                ? 'Cannot pin all participants — at least one must stay unpinned'
                : 'Pin'
          }
          // Top-right corner. For remote tiles we offset the pin to the
          // left of the existing volume + camera cluster (which sits at
          // right-2 and is ~64px wide with gap-2). OSPROD-84: bumped the
          // offset from 68px → 88px so there's ~22px of breathing room
          // between Pin and the speaker button — at 6px the pair read
          // as a single control group and users were misclicking pin
          // when they meant volume (and vice-versa), especially on
          // touchpads and small tiles. Self-tile has no volume/camera
          // cluster, so the pin can sit flush at right-2.
          className={`absolute top-2 ${isSelf ? 'right-2' : 'right-[88px]'} z-20 w-7 h-7 rounded-md backdrop-blur-sm flex items-center justify-center transition ${
            isPinned
              ? 'bg-primary text-primary-foreground opacity-100'
              : 'bg-black/60 text-white hover:bg-black/80 opacity-0 group-hover:opacity-100'
          } ${!isPinned && pinCapReached ? 'cursor-not-allowed opacity-50' : ''}`}
        >
          <Pin size={13} />
        </button>
      )}
    </div>
  )
}
